import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { api, mediaBlobUrl } from '../api';
import { DOC_READINESS, METER_SOURCE, METER_UNIT, REPORT_SEVERITY, REPORT_STATUS, REQUIRED_UNIT_DOCS, SERVICE_KIND, TIRE_LOCATION, TIRE_REASON, TRIP_STATUS, UNIT_CATEGORY, UNIT_CATEGORY_ORDER, UNIT_KIND, UNIT_METER, UNIT_OWNER, UNIT_STATUS, convertMeter, dateOnly, dateTime, defaultTireLocation, docReadiness, expiryBadge, platesText, serviceDue, serviceDuesFrom, timeAgo, unitAlerts, unitCategoryOf, unitKindLabel, unitName } from '../format';
import type { AuditEvent, CompanyInfo, CustomUnitKind, InspectionItemStatus, MeterReading, Session, TireLocation, TireRemoveReason, Trip, Unit, UnitCategory, UnitComponent, UnitDocument, UnitInput, UnitInspection, UnitInspectionInput, UnitInspectionKind, UnitMeterKind, UnitPlate, UnitReport, UnitReportInput, UnitReportSeverity, UnitReportStatus, UnitService, UnitServiceInput, UnitServiceKind, UnitStatus, UnitTire, UnitTireInput } from '../types';
import { Badge, Btn, Card, Empty, Field, Icon, Modal, Notice, Select, Skeleton, TextArea } from '../components/ui';
import { INSPECTION_ITEM_LABEL, INSPECTION_KIND_LABEL, inspectionGroupsFor } from '../inspection';
import { tirePositionsForKind, tirePositionLabel, type TirePosition } from '../tirePositions';
import { AuditRow } from './Audit';
import { DOC_CATALOG, DocList, DocumentForm, dateInputToIso, isoToDateInput, photoFromFile } from './Documents';
import { useDensity } from '../useDensity';
import { usePortal } from '../App';

// «Unidades» de la oficina: la flota separada en Vehículos / Remolques /
// Equipo. Vista de tarjetas (default) o tabla densa — el toggle se guarda en
// localStorage. La ficha lleva datos completos, componentes (el reefer de la
// caja refrigerada), lecturas del contador, estado y sus documentos.
// Las unidades llegan en el snapshot como companyUnits (vacío en backends
// viejos — el estado vacío no rompe nada).

const VIEW_KEY = 'cargados-units-view';

type ViewMode = 'cards' | 'table';

const sortUnits = (list: Unit[]) => list.slice().sort((a, b) =>
  a.internalNumber.localeCompare(b.internalNumber, 'es') || platesText(a).localeCompare(platesText(b), 'es'));

/** Icono del tipo de unidad: integrado usa su glifo; el personalizado cae al
    de su categoría. */
function kindIcon(unit: Unit): 'truck' | 'cube' | 'snow' | 'wrench' {
  const builtin = UNIT_KIND[unit.kind]?.icon;
  if (builtin === 'snow') return 'snow';
  if (builtin === 'truck') return 'truck';
  if (unit.category === 'equipment' || unit.kind === 'reefer' || unit.kind === 'equipo') return 'wrench';
  return 'cube';
}

/** Captura de lectura del contador con cambio km/mi: el odómetro de la
    unidad puede marcar millas aunque la ficha lleve km — se captura en
    cualquiera y `value` sale ya en la unidad propia (convertMeter). Solo
    km/mi traen toggle (.seg); horas y «sin contador» se capturan directo.
    Devuelve el texto controlado, el toggle para poner junto al input, la
    etiqueta de la unidad de captura, la pista «se guarda en X» cuando
    difieren y toCapture(n) para enseñar mínimos guardados en la unidad
    elegida. */
function useMeterInput(meterKind: UnitMeterKind | undefined, initial = '') {
  const kind = meterKind === 'km' || meterKind === 'mi' ? meterKind : undefined;
  const [text, setText] = useState(initial);
  const [unit, setUnit] = useState<'km' | 'mi'>(kind ?? 'km');
  // Si la unidad cambia de tipo de contador con el modal abierto (km→horas),
  // el texto y la unidad de captura previos ya no aplican — reiniciar.
  const prevKind = useRef(meterKind);
  useEffect(() => {
    if (prevKind.current === meterKind) return;
    prevKind.current = meterKind;
    setText('');
    setUnit(kind ?? 'km');
  }, [meterKind, kind]);
  const typed = text.trim();
  const num = typed ? Number(typed) : NaN;
  const valid = !typed || (Number.isFinite(num) && num >= 0);
  const value = valid && typed ? (kind ? convertMeter(num, unit, kind) : num) : null;
  const label = kind ? METER_UNIT[unit] : METER_UNIT[meterKind ?? 'none'];
  const seg = kind ? <div className="seg" role="group" aria-label="Unidad de la lectura">
    {(['km', 'mi'] as const).map(u => <button key={u} type="button"
      className={`seg-btn${unit === u ? ' on' : ''}`} aria-pressed={unit === u}
      onClick={() => setUnit(u)}>{METER_UNIT[u]}</button>)}
  </div> : null;
  // Un valor ya guardado (unidad propia) expresado en la unidad de captura.
  const toCapture = (n: number) => kind ? convertMeter(n, kind, unit) : n;
  const hint = kind && unit !== kind ? `Se guarda en ${UNIT_METER[kind].toLowerCase()}.` : '';
  return { text, setText, unit, value, valid, label, seg, toCapture, hint };
}

/** «Unidades» de la vista de oficina: secciones por categoría + toggle de
    vista; el título y el alta viven en el encabezado de sección (onAdd).
    `openUnitId` es el deep-link del feed del Tablero: al llegar un id se
    abre su ficha y `onOpened` lo consume para no reabrirla al volver. */
export function UnitsCard({ units, docs, members, onAdd, openUnitId, onOpened }: {
  units: Unit[]; docs: UnitDocument[]; members: Session[]; onAdd(): void;
  openUnitId?: string | null; onOpened?(): void;
}) {
  const { busy, data } = usePortal();
  const company = data?.company ?? null;
  const [viewing, setViewing] = useState<string | null>(null);
  const [editing, setEditing] = useState<Unit | null>(null);
  const [view, setView] = useState<ViewMode>(() => {
    try { return localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'cards'; } catch { return 'cards'; }
  });
  // Densidad «Cómoda / Compacta» — preferencia compartida con Viajes,
  // Documentos y Equipo (useDensity).
  const { dense, seg: densitySeg } = useDensity();

  const byId = useMemo(() => new Map(units.map(u => [u.id, u])), [units]);
  const groups = useMemo(() => UNIT_CATEGORY_ORDER
    .map(cat => ({ cat, list: sortUnits(units.filter(u => unitCategoryOf(u, company) === cat)) }))
    .filter(g => g.list.length > 0), [units, company]);
  const viewingUnit = viewing ? byId.get(viewing) : undefined;

  const pickView = (v: ViewMode) => { setView(v); try { localStorage.setItem(VIEW_KEY, v); } catch { /* sin persistencia */ } };

  // Deep-link del feed: abrir la ficha pedida por el padre y avisarle para
  // que consuma el id — si no, cada visita a Unidades la reabriría.
  useEffect(() => {
    if (!openUnitId) return;
    if (byId.has(openUnitId)) setViewing(openUnitId);
    onOpened?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openUnitId]);

  // «En viaje»: el viaje abierto (asignado o en curso) que lleva la unidad
  // como tracto o remolque — alimenta la línea de la tarjeta y la tabla.
  const placeName = useMemo(() => {
    const map = new Map([...(data?.places ?? []), ...(data?.companyPlaces ?? [])].map(p => [p.id, p.name]));
    return (pid: string) => map.get(pid) ?? 'Parada';
  }, [data?.places, data?.companyPlaces]);
  const openTripByUnit = useMemo(() => {
    const map = new Map<string, Trip>();
    for (const t of data?.companyTrips ?? []) {
      if (t.status !== 'assigned' && t.status !== 'active') continue;
      if (t.tractorUnitId && !map.has(t.tractorUnitId)) map.set(t.tractorUnitId, t);
      if (t.trailerUnitId && !map.has(t.trailerUnitId)) map.set(t.trailerUnitId, t);
    }
    return map;
  }, [data?.companyTrips]);

  // Unidades con algo por atender (estado parado, servicio vencido o próximo,
  // documentos, reportes): la tira de arriba las resume y abre su ficha.
  const attention = useMemo(() => units
    .map(u => ({ unit: u, alerts: unitAlerts(u, docs, data?.companyUnitReports ?? [], data?.companyUnitServices ?? []) }))
    .filter(x => x.alerts.length > 0), [units, docs, data?.companyUnitReports, data?.companyUnitServices]);

  return <>
    {!units.length && <Card><Empty icon="truck" title="Sin unidades" message="Registra el primer vehículo o remolque de tu empresa para llevar sus documentos y mantenimiento."
      action={<Btn small busy={busy} onClick={onAdd}>Agregar unidad</Btn>} /></Card>}

    {/* Sin role="status": la tira lleva botones interactivos — una
        live-region no debe contener controles. */}
    {attention.length > 0 && <div className={`ualerts${dense ? ' dense' : ''}`}>
      {attention.map(({ unit, alerts }) => <button key={unit.id} type="button" className="ualert" onClick={() => setViewing(unit.id)}>
        <span className="uname">{unitName(unit, company)}</span>
        <span className="tiny muted">{alerts.slice(0, 2).map(a => a.text).join(' · ')}{alerts.length > 2 ? ` · +${alerts.length - 2}` : ''}</span>
        <Icon name="chevronRight" size={15} />
      </button>)}
    </div>}

    {units.length > 0 && <>
      {/* Barra de la lista: vista y densidad a la izquierda (la densidad es
          texto plano — el set Icon no trae metáfora para ella), conteo a la
          derecha. Visible también en móvil: las tarjetas se densifican igual. */}
      <div className="u-toolbar">
        <div className="row">
          <div className="seg" role="group" aria-label="Vista de unidades">
            <button type="button" className={`seg-btn${view === 'cards' ? ' on' : ''}`} aria-pressed={view === 'cards'} onClick={() => pickView('cards')}><Icon name="grid" size={15} />Tarjetas</button>
            <button type="button" className={`seg-btn${view === 'table' ? ' on' : ''}`} aria-pressed={view === 'table'} onClick={() => pickView('table')}><Icon name="list" size={15} />Tabla</button>
          </div>
          {densitySeg}
        </div>
        <span className="tiny muted">{units.length} {units.length === 1 ? 'unidad' : 'unidades'}</span>
      </div>

      {view === 'cards' && groups.map(g => <section key={g.cat} className={`u-group${dense ? ' dense' : ''}`} aria-label={UNIT_CATEGORY[g.cat].plural}>
        <h3 className="u-group-title">{UNIT_CATEGORY[g.cat].plural}<span className="cnt"> · {g.list.length}</span></h3>
        <div className="ucards">
          {g.list.map(u => <UnitTile key={u.id} unit={u} company={company} parent={u.parentUnitId ? byId.get(u.parentUnitId) : undefined}
            trip={openTripByUnit.get(u.id)} placeName={placeName} onOpen={() => setViewing(u.id)} />)}
        </div>
      </section>)}

      {view === 'table' && <Card className={dense ? 'dense' : undefined}>
        <div className="table-scroll">
          <table className="table">
            <thead><tr><th>Unidad</th><th>Marca</th><th>Modelo</th><th>Año</th><th>Placas</th><th>Contador</th><th>Próximo servicio</th><th>Estado</th></tr></thead>
            <tbody>
              {groups.flatMap(g => g.list).map(u => {
                const due = serviceDue(u);
                const st = UNIT_STATUS[u.status] ?? UNIT_STATUS.active;
                const trip = openTripByUnit.get(u.id);
                return <tr key={u.id} className="clickable" onClick={() => setViewing(u.id)}>
                  <td>
                    <div className="name">{unitName(u, company)}</div>
                    <div className="tiny muted">{UNIT_CATEGORY[unitCategoryOf(u, company)].label}{u.parentUnitId && byId.get(u.parentUnitId) ? ` · de ${unitName(byId.get(u.parentUnitId)!, company)}` : ''}</div>
                    {trip && <div className="tiny u-trip"><Icon name="route" size={11} />{trip.stops.map(s => placeName(s.placeId)).join(' → ') || 'En viaje'}</div>}
                  </td>
                  <td className="small" data-label="Marca">{u.brand || '—'}</td>
                  <td className="small" data-label="Modelo">{u.model || '—'}</td>
                  <td className="small" data-label="Año">{u.year ?? '—'}</td>
                  <td className="small" data-label="Placas">{platesText(u) || '—'}</td>
                  <td className="small" data-label="Contador">
                    {u.meterKind === 'none' ? '—'
                      : u.meter ? `${u.meter.value.toLocaleString('es-MX')} ${METER_UNIT[u.meterKind]}`
                      : 'Sin lectura'}
                  </td>
                  <td className="small" data-label="Próximo servicio">
                    {!due.length ? '—' : due.map((d, i) =>
                      <div key={i} className={d.overdue ? 'overdue' : undefined}>{d.text}</div>)}
                  </td>
                  <td data-label="Estado"><Badge tone={st.tone}>{st.label}</Badge></td>
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      </Card>}
    </>}

    {viewing && <UnitDetailModal unit={viewingUnit} units={units} docs={docs} members={members}
      onClose={() => setViewing(null)} onEdit={u => { setViewing(null); setEditing(u); }} />}
    {editing && <UnitForm unit={editing} units={units} onClose={() => setEditing(null)} />}
  </>;
}

/** Tarjeta de unidad en la vista de mosaico: icono del tipo, número interno,
    marca·modelo·año, contador y próximo servicio. Click abre la ficha. */
function UnitTile({ unit, company, parent, trip, placeName, onOpen }: {
  unit: Unit; company: Pick<CompanyInfo, 'customUnitKinds'> | null | undefined;
  parent?: Unit; trip?: Trip; placeName(id: string): string; onOpen(): void;
}) {
  const st = UNIT_STATUS[unit.status] ?? UNIT_STATUS.active;
  const due = serviceDue(unit);
  const reefer = (unit.components ?? []).find(c => c.kind === 'reefer');
  const specs = [unit.brand, unit.model, unit.year].filter(Boolean).join(' ');
  const meterLine = unit.meterKind === 'none' ? '' : unit.meter ? `${unit.meter.value.toLocaleString('es-MX')} ${METER_UNIT[unit.meterKind]}` : 'Sin lectura';
  return <button type="button" className="ucard" onClick={onOpen} aria-label={`${unitName(unit, company)}, ${st.label}`}>
    <UnitCover unit={unit} />
    <span className="umain">
      <span className="uhead">
        <span className="uname">{unit.internalNumber || platesText(unit) || unitKindLabel(unit, company)}</span>
        <Badge tone={st.tone}>{st.label}</Badge>
      </span>
      <span className="urow">{unitKindLabel(unit, company)}{specs ? ` · ${specs}` : ''}{parent ? ` · de ${unitName(parent, company)}` : ''}</span>
      <span className="urow muted">{[platesText(unit), meterLine].filter(Boolean).join(' · ') || 'Sin placas registradas'}</span>
      {reefer && <span className="urow muted">Reefer: {[reefer.brand, reefer.model].filter(Boolean).join(' ') || 'instalado'}{reefer.meter ? ` · ${reefer.meter.value.toLocaleString('es-MX')} h` : ''}</span>}
      {trip && <span className="urow trip"><Icon name="route" size={13} />En viaje · {trip.stops.map(s => placeName(s.placeId)).join(' → ') || 'Viaje'}</span>}
      {due.length > 0 && <span className={`urow due${due.some(d => d.overdue) ? ' over' : ''}`}>{due.map(d => d.text).join(' · ')}</span>}
    </span>
  </button>;
}

type UnitTab = 'general' | 'mantenimiento' | 'llantas' | 'reportes' | 'documentos' | 'bitacora';

/** Ficha de la unidad como ventana con pestañas: encabezado fijo (icono,
    nombre, estado) y cuatro vistas — General (foto, placas, identidad,
    salud, acciones, viajes), Contador (lectura, componentes/reefer,
    historial), Reportes (fallas con transiciones) y Documentos. */
function UnitDetailModal({ unit, units, docs, members, onClose, onEdit }: {
  unit: Unit | undefined; units: Unit[]; docs: UnitDocument[]; members: Session[];
  onClose(): void; onEdit(unit: Unit): void;
}) {
  const { token, busy, mutate, data, session, error, dismiss, authFail } = usePortal();
  const company = data?.company ?? null;
  const isOffice = data?.company?.role === 'office' || session.role === 'admin';
  const [tab, setTab] = useState<UnitTab>('general');
  const meterIn = useMeterInput(unit?.meterKind);
  const [readings, setReadings] = useState<MeterReading[] | null>(null);
  const [readingsFailed, setReadingsFailed] = useState(false);
  const [addingDoc, setAddingDoc] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [statusChange, setStatusChange] = useState<UnitStatus | null>(null);
  const [serviceForm, setServiceForm] = useState<null | { mode: 'add' } | { mode: 'edit', service: UnitService }>(null);
  const [inspecting, setInspecting] = useState<UnitInspectionKind | null>(null);
  const [printInspection, setPrintInspection] = useState<UnitInspection | null>(null);
  const [audit, setAudit] = useState<AuditEvent[] | null>(null);
  const [auditFailed, setAuditFailed] = useState(false);
  const [printing, setPrinting] = useState(false);
  // Llantas: el snapshot trae las activas (companyUnitTires); el historial
  // completo se pide perezoso al abrir la pestaña «Llantas» junto al stock
  // remontable de la empresa (listTireStock).
  const [tireForm, setTireForm] = useState<null | { position: TirePosition; editing?: UnitTire }>(null);
  const [tireList, setTireList] = useState<UnitTire[] | null>(null);
  const [tiresFailed, setTiresFailed] = useState(false);
  const [tireStock, setTireStock] = useState<UnitTire[] | null>(null);
  const [stockFailed, setStockFailed] = useState(false);
  // Modos del diagrama: rotación encadenada (origen → destino → confirmar)
  // y «elige posición» para montar un casco de bodega.
  const [rotateMode, setRotateMode] = useState(false);
  const [rotateFrom, setRotateFrom] = useState<string | null>(null);
  const [swapPair, setSwapPair] = useState<{ from: TirePosition; to: TirePosition } | null>(null);
  const [mountStock, setMountStock] = useState<UnitTire | null>(null);
  const [locTire, setLocTire] = useState<UnitTire | null>(null);
  // Distingue «canceló el formulario» de «guardó y cerró»: solo el primero
  // devuelve el casco a la mano (modo «elige posición»).
  const tireSavedRef = useRef(false);

  const unitId = unit?.id ?? '';
  const memberName = useMemo(() => {
    const map = new Map(members.map(m => [m.uid, m.displayName || m.email]));
    return (uid: string) => map.get(uid) ?? 'la oficina';
  }, [members]);

  const loadReadings = async () => {
    try {
      const { readings } = await api<{ readings: MeterReading[] }>('listMeterReadings', { unitId }, token);
      setReadings(readings ?? []); setReadingsFailed(false);
    } catch (e) { if (authFail(e)) return; setReadings(null); setReadingsFailed(true); }
  };
  // `data` cambia de identidad tras cada mutate()/refresh: recarga lecturas,
  // bitácora y llantas para no enseñar data perezosa obsoleta. `meterKind`
  // además: al cambiar km→h sin recargar quedaban lecturas de km rotuladas
  // como horas. Las cargas perezosas no tocan `data` — no hay ciclo.
  useEffect(() => {
    const metered = unit?.meterKind !== 'none' || (unit?.components ?? []).some(c => c.meterKind !== 'none');
    if (unitId && metered) void loadReadings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, unitId, unit?.meterKind, data]);

  const loadAudit = async () => {
    try {
      const { entries } = await api<{ entries: AuditEvent[] }>('listUnitAudit', { unitId }, token);
      setAudit(entries ?? []); setAuditFailed(false);
    } catch (e) { if (authFail(e)) return; setAudit(null); setAuditFailed(true); }
  };
  useEffect(() => {
    if (unitId && tab === 'bitacora' && !auditFailed) void loadAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, unitId, tab, data, auditFailed]);

  const loadTires = async () => {
    try {
      const { tires } = await api<{ tires: UnitTire[] }>('listUnitTires', { unitId }, token);
      setTireList(tires ?? []); setTiresFailed(false);
    } catch (e) { if (authFail(e)) return; setTiresFailed(true); }
  };
  useEffect(() => {
    if (unitId && tab === 'llantas' && !tiresFailed) void loadTires();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, unitId, tab, data, tiresFailed]);

  // Stock de cascos desmontados recuperables (yard+retread) de TODA la
  // empresa: perezoso con la pestaña, igual que listUnitTires. La acción es
  // solo de oficina/admin — sin ese rol ni se pide ni se muestra la tira.
  const loadTireStock = async () => {
    try {
      const { tires } = await api<{ tires: UnitTire[] }>('listTireStock', {}, token);
      setTireStock(tires ?? []); setStockFailed(false);
    } catch (e) { if (authFail(e)) return; setStockFailed(true); }
  };
  useEffect(() => {
    if (unitId && isOffice && tab === 'llantas' && !stockFailed) void loadTireStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, unitId, tab, isOffice, data, stockFailed]);

  // Escape sale del modo del diagrama (rotación o «montar de bodega»). Fase
  // de captura + stopPropagation: gana al keydown del Modal padre para que
  // la ficha no se cierre de un golpe. Con el confirm de swap o el
  // formulario abiertos, el Modal correspondiente maneja su propio Escape.
  useEffect(() => {
    if ((!rotateMode && !mountStock) || swapPair || tireForm) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setRotateMode(false); setRotateFrom(null); setMountStock(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [rotateMode, mountStock, swapPair, tireForm]);

  // Los hooks van ANTES del return condicional: si la unidad sale del
  // snapshot con la ficha abierta (venta, sync), renderizar un hook menos
  // haría fallar React con «Rendered fewer hooks than expected».
  const placeName = useMemo(() => {
    const map = new Map([...(data?.places ?? []), ...(data?.companyPlaces ?? [])].map(p => [p.id, p.name]));
    return (pid: string) => map.get(pid) ?? 'Parada';
  }, [data?.places, data?.companyPlaces]);
  // Servicios de la unidad: el snapshot trae los recientes; el modal usa esa
  // lista para próximos por tipo y para el historial corto (listUnitServices
  // existe para historial completo si hiciera falta).
  const services = useMemo(() => (data?.companyUnitServices ?? []).filter(s => s.unitId === unitId), [data?.companyUnitServices, unitId]);
  // Inspecciones pre/post-viaje: el snapshot trae las recientes (desc por
  // createdAt); la pestaña muestra las últimas 5 y avisa cuántas quedan.
  const unitInspections = useMemo(() => (data?.companyUnitInspections ?? []).filter(i => i.unitId === unitId), [data?.companyUnitInspections, unitId]);
  const inspections = unitInspections.slice(0, 5);

  if (!unit) return <Modal title="Unidad" onClose={onClose}><Empty title="La unidad ya no existe" /></Modal>;

  const st = UNIT_STATUS[unit.status] ?? UNIT_STATUS.active;
  const category = unitCategoryOf(unit, company);
  const unitDocs = docs.filter(d => d.subjectType === 'unit' && d.subjectId === unit.id);
  // Checklist «Para cruzar»: los documentos requeridos según el país de las
  // placas — la unidad de cruce (MX+US) pide ambas listas; sin placas se
  // muestran las dos para que el alta de placa no deje huecos sin avisar.
  const plateCountries = new Set(unit.plates.map(p => p.country));
  const requiredDocs: string[] = [
    ...(plateCountries.size === 0 || plateCountries.has('MX') ? REQUIRED_UNIT_DOCS.mx : []),
    ...(plateCountries.size === 0 || plateCountries.has('US') ? REQUIRED_UNIT_DOCS.us : []),
  ];
  // Cuántos del checklist ya quedaron vigentes — encabeza «Para cruzar».
  const crossReady = requiredDocs.filter(t => docReadiness(unitDocs, t) === 'vigente').length;
  const children = units.filter(u => u.parentUnitId === unit.id);
  const parent = unit.parentUnitId ? units.find(u => u.id === unit.parentUnitId) : undefined;
  const due = serviceDue(unit);

  // Salud de un vistazo: documentos por vencer/vencidos + reportes abiertos.
  const docsAttention = unitDocs.filter(d => !d.supersededBy && (expiryBadge(d.expiresAt) || d.status === 'rejected'));
  const unitReports = (data?.companyUnitReports ?? []).filter(r => r.unitId === unit.id);
  const openReports = unitReports.filter(r => r.status !== 'resolved');
  const worstSeverity = openReports.some(r => r.severity === 'high') ? 'high' : openReports.some(r => r.severity === 'medium') ? 'medium' : 'low';

  // Viajes recientes de esta unidad (el tracto o la caja que arrastró).
  const unitTrips = (data?.companyTrips ?? [])
    .filter(t => t.tractorUnitId === unit.id || t.trailerUnitId === unit.id)
    .sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))
    .slice(0, 5);
  // El viaje abierto (asignado/en curso) que la lleva ahora — aviso en General.
  const openTrip = (data?.companyTrips ?? []).find(t =>
    (t.status === 'assigned' || t.status === 'active') &&
    (t.tractorUnitId === unit.id || t.trailerUnitId === unit.id));
  // Salidas con defectos graves autorizadas por la oficina en que participó
  // esta unidad (tracto o caja) — la bitácora las lista arriba de la línea
  // de tiempo como expediente de autorizaciones.
  const overrideTrips = (data?.companyTrips ?? [])
    .filter(t => (t.tractorUnitId === unit.id || t.trailerUnitId === unit.id) && t.defectOverride)
    .sort((a, b) => String(b.defectOverride?.at ?? '').localeCompare(String(a.defectOverride?.at ?? '')));

  // Transiciones posibles según estado: vendida solo se reactiva (por si fue error).
  const targets: UnitStatus[] =
    unit.status === 'active' ? ['shop', 'down', 'sold']
    : unit.status === 'sold' ? ['active']
    : ['active', ...(['shop', 'down'] as UnitStatus[]).filter(s => s !== unit.status), 'sold'];
  const STATUS_LABEL: Record<UnitStatus, string> = { active: 'Reactivar', shop: 'Enviar a taller', down: 'Fuera de servicio', sold: 'Marcar vendida' };

  // Operador habitual: el miembro que tiene esta unidad como su tracto/caja
  // habitual (setMemberUnits). Reasignar limpia al portador anterior.
  const isVehicle = category === 'vehicle';
  const isAttachment = category === 'attachment';
  const holder = (isVehicle || isAttachment)
    ? members.find(m => (isVehicle ? m.defaultTractorId : m.defaultTrailerId) === unit.id)
    : undefined;
  const drivers = members.filter(m => m.companyRole !== 'office' && m.status === 'active');
  const assignDriver = (uid: string) => {
    const ops: Promise<unknown>[] = [];
    if (holder && holder.uid !== uid) {
      ops.push(api('setMemberUnits', {
        uid: holder.uid,
        tractorUnitId: isVehicle ? null : holder.defaultTractorId ?? null,
        trailerUnitId: isVehicle ? holder.defaultTrailerId ?? null : null,
      }, token));
    }
    const next = members.find(m => m.uid === uid);
    if (next) {
      ops.push(api('setMemberUnits', {
        uid: next.uid,
        tractorUnitId: isVehicle ? unit.id : next.defaultTractorId ?? null,
        trailerUnitId: isVehicle ? next.defaultTrailerId ?? null : unit.id,
      }, token));
    }
    void mutate(() => Promise.all(ops), uid ? 'Operador de la unidad actualizado.' : 'Unidad sin operador habitual.');
  };

  const lastValue = unit.meter?.value ?? null;
  const meterUnit = METER_UNIT[unit.meterKind];
  // meterIn.value ya llega convertido a la unidad propia — el mínimo se
  // compara contra la última lectura guardada (misma unidad).
  const readingValue = meterIn.value;
  const readingValid = readingValue !== null && (lastValue === null || readingValue >= lastValue);
  const saveReading = () => {
    if (readingValue === null) return;
    void mutate(() => api('saveMeterReading', { unitId: unit.id, value: readingValue }, token), 'Lectura registrada.')
      .then(ok => { if (ok) { meterIn.setText(''); void loadReadings(); } });
  };

  // El historial mezcla lecturas de la unidad y de sus componentes: cada
  // bloque filtra las suyas.
  const unitReadings = (readings ?? []).filter(r => !r.componentId);

  // Mantenimiento agrupa estado (taller/fuera de servicio con motivo),
  // pendientes (servicio, docs, reportes), contador y componentes — aplica a
  // toda unidad, tenga o no medidor propio.
  const components = unit.components ?? [];
  const kindDues = serviceDuesFrom(unit, services);
  const pendCount = due.filter(d => d.overdue).length + kindDues.filter(d => d.overdue).length + docsAttention.length + openReports.length;

  // Llantas por posición: el diagrama se alimenta de las activas del
  // snapshot hasta que el listado completo (con historial) llega de
  // listUnitTires al abrir la pestaña.
  const tirePositions = tirePositionsForKind(unit.kind, category);
  const allTires = tireList !== null ? tireList : (data?.companyUnitTires ?? []).filter(t => t.unitId === unit.id);
  const activeTires = allTires.filter(t => t.unitId === unit.id && !t.removedAt);
  const tireHistory = allTires.filter(t => t.unitId === unit.id && t.removedAt)
    .sort((a, b) => String(b.removedAt).localeCompare(String(a.removedAt)));
  const tirePosOf = (t: UnitTire): TirePosition =>
    tirePositions.find(p => p.key === t.position) ?? { key: t.position, axle: 0, label: t.position };
  const posLabel = (key: string) => tirePositions.find(p => p.key === key)?.label ?? key;
  const exitTireMode = () => { setRotateMode(false); setRotateFrom(null); setMountStock(null); };

  // Toque de celda con modo activo. Rotar: el primer toque (ocupada) fija el
  // origen — tocarlo de nuevo lo suelta —; el segundo (ocupada o vacía) arma
  // el par que pide confirmación. Montar de bodega: solo las vacías
  // responden y abren el formulario de remontaje del casco elegido.
  const onTirePick = (pos: TirePosition) => {
    const tire = activeTires.find(t => t.position === pos.key);
    if (rotateMode) {
      if (!rotateFrom) { if (tire) setRotateFrom(pos.key); return; }
      if (pos.key === rotateFrom) { setRotateFrom(null); return; }
      const from = tirePositions.find(p => p.key === rotateFrom);
      if (from) setSwapPair({ from, to: pos });
      return;
    }
    if (mountStock && !tire) { setTireForm({ position: pos, editing: mountStock }); setMountStock(null); }
  };

  const tabs: { id: UnitTab; label: string; cnt?: number }[] = [
    { id: 'general', label: 'General' },
    { id: 'mantenimiento', label: 'Mantenimiento', cnt: pendCount || undefined },
    ...(tirePositions.length ? [{ id: 'llantas' as UnitTab, label: 'Llantas', cnt: activeTires.length || undefined }] : []),
    { id: 'reportes', label: 'Reportes', cnt: openReports.length },
    { id: 'documentos', label: 'Documentos', cnt: unitDocs.filter(d => !d.supersededBy).length },
    { id: 'bitacora', label: 'Bitácora' },
  ];

  // Cambio rápido de unidad del contador (km/mi/h): reenvía la ficha intacta.
  const setMeterKind = (mk: UnitMeterKind) => {
    if (mk === unit.meterKind) return;
    const input: UnitInput = {
      kind: unit.kind, plates: unit.plates, internalNumber: unit.internalNumber,
      brand: unit.brand, model: unit.model, year: unit.year, vin: unit.vin,
      owner: unit.owner, meterKind: mk, parentUnitId: unit.parentUnitId,
      components: components.map(c => ({
        id: c.id, kind: c.kind, label: c.label, brand: c.brand, model: c.model,
        serial: c.serial, meterKind: c.meterKind,
        maintenanceMeterInterval: c.maintenanceMeterInterval,
        maintenanceDaysInterval: c.maintenanceDaysInterval,
      })),
      maintenanceMeterInterval: unit.maintenanceMeterInterval,
      maintenanceDaysInterval: unit.maintenanceDaysInterval,
    };
    void mutate(() => api('saveUnit', { unitId: unit.id, input }, token), 'Contador actualizado.');
  };
  const activeTab = tabs.some(t => t.id === tab) ? tab : 'general';

  return <Modal title={unitName(unit, company)} onClose={onClose} wide>
    <div className="uhead-row">
      <span className={`uicon lg${unit.status !== 'active' ? ' off' : ''}`}><Icon name={kindIcon(unit)} size={26} /></span>
      <div className="grow">
        <div className="title">{[unit.brand, unit.model, unit.year].filter(Boolean).join(' ') || unitKindLabel(unit, company)}</div>
        <div className="small muted">{UNIT_CATEGORY[category].label} · {unitKindLabel(unit, company)}{parent ? ` · adosado a ${unitName(parent, company)}` : ''}</div>
      </div>
      <Badge tone={st.tone}>{st.label}</Badge>
    </div>

    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}

    <div className="seg grow" role="tablist" aria-label="Secciones de la unidad">
      {tabs.map(t => <button key={t.id} type="button" role="tab" aria-selected={activeTab === t.id}
        className={`seg-btn${activeTab === t.id ? ' on' : ''}`} onClick={() => setTab(t.id)}>
        {t.label}{t.cnt ? <span className="cnt">{t.cnt}</span> : null}
      </button>)}
    </div>

    {activeTab === 'general' && <div className="ugrid">
      <aside className="ugrid-side">
        <UnitGallery unit={unit} />
        {unit.plates.length > 0 && <div className="platechips">
          {unit.plates.map((p, i) => <span key={i} className="platechip"><span className="cty">{p.country}</span>{p.number}</span>)}
        </div>}
        <div className="vstack">
          <Btn small tone="secondary" busy={busy} onClick={() => onEdit(unit)}>Editar datos</Btn>
          <Btn small tone="ghost" onClick={() => setPrinting(true)}>Exportar ficha</Btn>
        </div>
      </aside>

      <div className="ugrid-main">
        <div className="ustats">
          <div className="ustat">
            <span className="ulabel">Contador{unit.meterKind !== 'none' ? ` · ${METER_UNIT[unit.meterKind]}` : ''}</span>
            <span className="uval">{unit.meterKind === 'none' ? 'Sin contador'
              : unit.meter ? `${unit.meter.value.toLocaleString('es-MX')} ${meterUnit}` : 'Sin lectura'}</span>
            {unit.meter && <span className="tiny muted">{dateOnly(unit.meter.at)}</span>}
          </div>
          <div className="ustat">
            <span className="ulabel">Próximo servicio</span>
            <span className={`uval${due.some(d => d.overdue) ? ' u-bad' : due.length ? '' : ' muted'}`}>
              {due.length ? due[0].text : '—'}</span>
            {due.length > 1 && <span className="tiny muted">{due[1].text}</span>}
          </div>
          <div className="ustat">
            <span className="ulabel">Documentos</span>
            <span className={`uval${docsAttention.length ? ' u-warn' : ''}`}>
              {unitDocs.filter(d => !d.supersededBy).length || '—'}</span>
            <span className="tiny muted">{docsAttention.length ? `${docsAttention.length} por atender` : 'al corriente'}</span>
          </div>
          <div className="ustat">
            <span className="ulabel">Reportes</span>
            <span className={`uval${openReports.length ? (worstSeverity === 'high' ? ' u-bad' : ' u-warn') : ''}`}>
              {openReports.length || 'Ninguno abierto'}</span>
            <span className="tiny muted">{openReports.length ? REPORT_SEVERITY[worstSeverity].label.toLowerCase() : 'sin fallas'}</span>
          </div>
        </div>

        {openTrip && <Notice tone="info" icon="route">
          <span>En viaje: {openTrip.stops.map(s => placeName(s.placeId)).join(' → ') || 'Viaje'} · {openTrip.driverName || 'Chofer'}</span>
        </Notice>}

        <dl className="kv">
          <dt>Número interno</dt><dd>{unit.internalNumber || '—'}</dd>
          <dt>Marca</dt><dd>{unit.brand || '—'}</dd>
          <dt>Modelo</dt><dd>{unit.model || '—'}</dd>
          <dt>Año</dt><dd>{unit.year ?? '—'}</dd>
          <dt>VIN</dt><dd className="mono">{unit.vin || '—'}</dd>
          <dt>Propiedad</dt><dd>{UNIT_OWNER[unit.owner]}</dd>
          {children.length > 0 && <><dt>Equipo adosado</dt><dd>{children.map(c => `${unitName(c, company)} (${UNIT_STATUS[c.status].label.toLowerCase()})`).join(' · ')}</dd></>}
        </dl>

        {/* Operador habitual: la misma relación «unidad habitual» del miembro,
            vista desde la unidad — autollena esta unidad al asignarle viaje. */}
        {(isVehicle || isAttachment) && <div className="field">
          <label htmlFor="udm-driver">Operador habitual</label>
          <select className="select" id="udm-driver" value={holder?.uid ?? ''} disabled={busy}
            onChange={e => assignDriver(e.target.value)}>
            <option value="">Sin operador asignado</option>
            {drivers.map(m => <option key={m.uid} value={m.uid}>{m.displayName || m.email}</option>)}
            {/* El portador actual puede no ser elegible (inactivo u oficina):
                sin su opción el select fingiría «Sin operador» teniendo valor. */}
            {holder && !drivers.some(m => m.uid === holder.uid) &&
              <option value={holder.uid}>{holder.displayName || holder.email}</option>}
          </select>
          <span className="hint">Autollenan el vehículo y el remolque al asignarle un viaje al operador.</span>
        </div>}

        {unitTrips.length > 0 && <>
          <h3 className="card-title sm">Viajes recientes</h3>
          <div className="list">
            {unitTrips.map(t => <div className="item" key={t.id}>
              <div className="row between multiline">
                <span className="small name">{t.stops.map(s => placeName(s.placeId)).join(' → ') || 'Viaje'}</span>
                <Badge tone={TRIP_STATUS[t.status]?.tone ?? 'muted'}>{TRIP_STATUS[t.status]?.label ?? t.status}</Badge>
              </div>
              <div className="tiny muted">{t.driverName || 'Chofer'} · {dateTime(t.updatedAt)}</div>
            </div>)}
          </div>
        </>}
      </div>
    </div>}

    {activeTab === 'mantenimiento' && <div className="ugrid-main">
      {/* Estado: taller/fuera de servicio piden motivo (ventana propia); el
          motivo y la fecha quedan aquí hasta reactivarla. */}
      <div className="row between multiline">
        <h3 className="card-title sm">Estado</h3>
        <Badge tone={st.tone}>{st.label}</Badge>
      </div>
      {unit.status !== 'active' && (unit.statusNote || unit.statusAt) && <Notice tone="warning">
        <span>{UNIT_STATUS[unit.status].label}{unit.statusAt ? ` desde ${dateOnly(unit.statusAt)}` : ''}{unit.statusNote ? ` — ${unit.statusNote}` : ''}</span>
      </Notice>}
      <div className="row multiline">
        {targets.map(t => <Btn key={t} small tone={t === 'active' ? 'secondary' : t === 'sold' ? 'danger' : 'ghost'} busy={busy} onClick={() => setStatusChange(t)}>{STATUS_LABEL[t]}</Btn>)}
      </div>

      {/* Pendientes: servicio por intervalo, próximos por tipo (del historial
          de servicios), documentos por atender y reportes abiertos. */}
      <h3 className="card-title sm">Pendientes</h3>
      {!due.length && !kindDues.length && !docsAttention.length && !openReports.length
        ? <div className="small muted">Sin pendientes — servicio, documentos y reportes al corriente.</div>
        : <div className="list">
            {due.map((d, i) => <div className="item" key={`svc-${i}`}>
              <div className="row between multiline">
                <span className={`small name${d.overdue ? ' u-bad' : ''}`}>{d.text}</span>
                <span className="tiny muted">Servicio</span>
              </div>
            </div>)}
            {kindDues.map((d, i) => <div className="item" key={`ksvc-${i}`}>
              <div className="row between multiline">
                <span className={`small name${d.overdue ? ' u-bad' : ''}`}>{d.label} <span className="muted">{d.text}</span></span>
                <span className="tiny muted">Servicio</span>
              </div>
            </div>)}
            {docsAttention.length > 0 && <div className="item">
              <div className="row between multiline">
                <span className="small name u-warn">{docsAttention.length} documento{docsAttention.length === 1 ? '' : 's'} por atender</span>
                <Btn small tone="ghost" onClick={() => setTab('documentos')}>Revisar</Btn>
              </div>
            </div>}
            {openReports.length > 0 && <div className="item">
              <div className="row between multiline">
                <span className={`small name${worstSeverity === 'high' ? ' u-bad' : ' u-warn'}`}>{openReports.length} reporte{openReports.length === 1 ? '' : 's'} abierto{openReports.length === 1 ? '' : 's'}</span>
                <Btn small tone="ghost" onClick={() => setTab('reportes')}>Revisar</Btn>
              </div>
            </div>}
          </div>}

      {/* Inspecciones pre/post-viaje: el walkaround ítem por ítem — sus
          defectos pueden convertirse en reporte (reportId queda ligado). */}
      <div className="row between multiline">
        <h3 className="card-title sm">Inspecciones</h3>
        <div className="hstack">
          <Btn small onClick={() => setInspecting('pretrip')}>Inspección pre-viaje</Btn>
          <Btn small tone="secondary" onClick={() => setInspecting('posttrip')}>Post-viaje</Btn>
        </div>
      </div>
      {!inspections.length
        ? <Empty icon="clipboard" title="Sin inspecciones registradas." />
        : <div className="list">
            {inspections.map(i => <InspectionRow key={i.id} inspection={i} onPrint={() => setPrintInspection(i)} />)}
            {unitInspections.length > inspections.length && <div className="tiny muted">Y {unitInspections.length - inspections.length} más en el historial.</div>}
          </div>}

      {/* Historial de servicios: lo que se le ha hecho a la unidad (aceite,
          frenos, inspección…) con lectura, taller y costo; de aquí salen los
          próximos por tipo de Pendientes. */}
      <div className="row between multiline">
        <h3 className="card-title sm">Servicios{services.length > 0 ? ` · ${services.length}` : ''}</h3>
        <Btn small tone="secondary" onClick={() => setServiceForm({ mode: 'add' })}>Registrar servicio</Btn>
      </div>
      {!services.length
        ? <div className="small muted">Sin servicios registrados — captura el primero para llevar el historial y programar el siguiente.</div>
        : <div className="list">
            {services.slice(0, 8).map(s => <ServiceRow key={s.id} service={s} unit={unit} memberName={memberName}
              onEdit={() => setServiceForm({ mode: 'edit', service: s })} />)}
            {services.length > 8 && <div className="tiny muted">Y {services.length - 8} más en el historial.</div>}
          </div>}

      {/* Contador: la unidad (km/mi/h) se elige aquí mismo — reenvía la ficha
          con el meterKind nuevo sin tocar lecturas ni servicios. */}
      <div className="row between multiline">
        <h3 className="card-title sm">Contador{unit.meterKind !== 'none' ? ` · ${UNIT_METER[unit.meterKind].toLowerCase()}` : ''}</h3>
        <div className="seg" role="group" aria-label="Unidad del contador">
          {(['km', 'mi', 'hours', 'none'] as UnitMeterKind[]).map(mk => <button key={mk} type="button"
            className={`seg-btn${unit.meterKind === mk ? ' on' : ''}`} disabled={busy}
            title={UNIT_METER[mk]} onClick={() => setMeterKind(mk)}>{mk === 'none' ? '—' : METER_UNIT[mk]}</button>)}
        </div>
      </div>
      {unit.meterKind === 'none' && <div className="small muted">Sin contador propio — activa km, mi u h para llevar lecturas.</div>}
      {unit.meterKind !== 'none' && <>
        <div className="row">
          <input className="input num wide" type="number" min={0} inputMode="numeric"
            placeholder={lastValue !== null ? `≥ ${meterIn.toCapture(lastValue).toLocaleString('es-MX')} ${meterIn.label}` : `Lectura en ${UNIT_METER[unit.meterKind].toLowerCase()}`}
            value={meterIn.text} onChange={e => meterIn.setText(e.target.value)} aria-label="Nueva lectura" />
          {meterIn.seg ?? <span className="small muted">{meterUnit}</span>}
          <Btn small busy={busy} disabled={!readingValid} onClick={saveReading}>Guardar lectura</Btn>
        </div>
        {meterIn.hint && <span className="hint">{meterIn.hint}</span>}
        {lastValue !== null && <span className="hint">El servicio solo acepta valores iguales o mayores a la última lectura ({lastValue.toLocaleString('es-MX')} {meterUnit}).</span>}

        {readings === null && !readingsFailed && <Skeleton rows={3} />}
        {readingsFailed && <Notice tone="warning">No se pudo cargar el historial de lecturas. <Btn small tone="ghost" onClick={() => { setReadingsFailed(false); setReadings(null); void loadReadings(); }}>Reintentar</Btn></Notice>}
        {unitReadings.length > 0 && <div className="list">
          {unitReadings.map(r => <div className="item" key={r.id}>
            <div className="row between multiline">
              {/* Lectura malformada del servicio → '—' en vez de «NaN km». */}
              <span className="small name">{Number.isFinite(r.value) ? `${r.value.toLocaleString('es-MX')} ${meterUnit}` : '—'}</span>
              <span className="tiny muted">{dateTime(r.at)}</span>
            </div>
            <div className="tiny muted">{memberName(r.reportedBy)} · {METER_SOURCE[r.source] ?? r.source}</div>
          </div>)}
        </div>}
        {readings !== null && !unitReadings.length && <div className="small muted">Sin lecturas registradas todavía.</div>}
      </>}

      {/* Componentes permanentes (reefer de la caja refrigerada): su propio
          contador, servicio y registro de lectura. */}
      {components.map(cmp => <ComponentCard key={cmp.id} unit={unit} component={cmp} readings={readings} memberName={memberName} />)}
    </div>}

    {statusChange && <UnitStatusModal unit={unit} target={statusChange} onClose={() => setStatusChange(null)} />}
    {serviceForm && <ServiceForm unit={unit} editing={serviceForm.mode === 'edit' ? serviceForm.service : null} onClose={() => setServiceForm(null)} />}
    {inspecting && <InspectionForm unit={unit} kind={inspecting} previousDefects={inspections[0]?.defects ?? []} onClose={() => setInspecting(null)} />}
    {printing && <UnitPacket unit={unit} docs={unitDocs} members={members} onClose={() => setPrinting(false)} />}
    {printInspection && <InspectionPacket inspection={printInspection} unit={unit} onClose={() => setPrintInspection(null)} />}

    {/* Llantas por posición: el diagrama por ejes muestra qué hueco lleva
        llanta y cómo viene de profundidad; la vacía ofrece «Montar» y la
        ocupada abre su ficha. El historial (desmontadas) se pide perezoso. */}
    {activeTab === 'llantas' && <div className="ugrid-main">
      <div className="row between multiline">
        <h3 className="card-title sm">Llantas montadas · {activeTires.length} de {tirePositions.length}</h3>
        <div className="hstack">
          {!rotateMode && !mountStock && <span className="tiny muted">Punto verde: profundidad sana · ámbar: cerca del mínimo · rojo: bajo mínimo DOT</span>}
          {isOffice && (rotateMode || mountStock
            ? <Btn small tone="ghost" onClick={exitTireMode}>Cancelar</Btn>
            : <Btn small tone="secondary" onClick={() => { setRotateMode(true); setRotateFrom(null); }}>Rotar</Btn>)}
        </div>
      </div>
      {/* Rotar: encadenado — cada swap vuelve a pedir origen hasta Cancelar/Esc.
          Montar de bodega: solo las vacías responden. */}
      {rotateMode && <Notice tone="info"><span>{rotateFrom
        ? `Origen: ${posLabel(rotateFrom)} — toca la posición destino (o una vacía para moverla).`
        : 'Modo rotar — toca la llanta que quieres mover.'}</span></Notice>}
      {mountStock && <Notice tone="info"><span>Montando {[mountStock.brand, mountStock.size].filter(Boolean).join(' ') || 'el casco'} — toca una posición vacía.</span></Notice>}
      <TireDiagram positions={tirePositions} tires={activeTires}
        mode={mountStock ? 'mount' : rotateMode ? 'rotate' : 'normal'}
        selectedKey={rotateMode ? rotateFrom : null}
        onMount={pos => setTireForm({ position: pos })}
        onOpen={t => setTireForm({ position: tirePosOf(t), editing: t })}
        onPick={onTirePick} />
      {tiresFailed && <Notice tone="warning">No se pudo cargar el detalle de llantas — el diagrama usa las activas del último resumen. <Btn small tone="ghost" onClick={() => { setTiresFailed(false); setTireList(null); void loadTires(); }}>Reintentar</Btn></Notice>}

      {/* Bodega/reencauche: cascos desmontados de TODA la empresa que se
          pueden remontar en esta unidad o mover de destino («Mover a…»).
          listTireStock es solo de oficina/admin — la tira entera se omite
          para el resto en lugar de quedar como aviso permanente. */}
      {isOffice && <>
        <div className="row between multiline">
          <h3 className="card-title sm">En bodega{tireStock ? ` · ${tireStock.length}` : ''}</h3>
        </div>
        {tireStock === null && !stockFailed && <Skeleton rows={2} />}
        {stockFailed && <Notice tone="warning">No se pudo cargar la bodega de llantas. <Btn small tone="ghost" onClick={() => { setStockFailed(false); setTireStock(null); void loadTireStock(); }}>Reintentar</Btn></Notice>}
        {tireStock !== null && !tireStock.length && <div className="small muted">Sin cascos recuperables — al desmontar a bodega o a reencauche aparecen aquí y se pueden remontar.</div>}
        {tireStock !== null && tireStock.length > 0 && <div className="list">
          {tireStock.map(t => {
            const src = units.find(u => u.id === t.unitId);
            const srcPos = src ? tirePositionLabel(src.kind, unitCategoryOf(src, company), t.position) : t.position;
            const loc = TIRE_LOCATION[t.location ?? 'yard'] ?? TIRE_LOCATION.yard;
            return <div className="item" key={t.id}>
              <div className="row between multiline">
                <div className="grow">
                  <div className="title sm">{[t.brand, t.size].filter(Boolean).join(' ') || 'Llanta'}{t.retread ? ' · REC' : ''}</div>
                  <div className="tiny muted">{[
                    src && src.id !== unit.id ? `De ${unitName(src, company)}` : '',
                    `Salió de ${srcPos}`,
                    t.removedTread32 !== null ? `${t.removedTread32}/32 final` : '',
                  ].filter(Boolean).join(' · ')}</div>
                </div>
                <div className="hstack">
                  <Badge tone={loc.tone}>{loc.label}</Badge>
                  <Btn small tone="secondary" onClick={() => { setMountStock(t); setRotateMode(false); setRotateFrom(null); }}>Montar</Btn>
                  <Btn small tone="ghost" onClick={() => setLocTire(t)}>Mover a…</Btn>
                </div>
              </div>
            </div>;
          })}
        </div>}
      </>}

      <h3 className="card-title sm">Historial{tireHistory.length ? ` · ${tireHistory.length}` : ''}</h3>
      {tireList === null && !tiresFailed && <Skeleton rows={3} />}
      {tireList !== null && !tireHistory.length && <div className="small muted">Sin llantas desmontadas todavía — al quitar una queda aquí con su motivo.</div>}
      {tireHistory.length > 0 && <div className="list">
        {tireHistory.slice(0, 10).map(t => {
          const loc = t.location ? TIRE_LOCATION[t.location] : undefined;
          return <div className="item" key={t.id}>
            <div className="row between multiline">
              <div className="grow">
                <div className="title sm">{[t.brand, t.size].filter(Boolean).join(' ') || 'Llanta'}{t.retread ? ' · REC' : ''}</div>
                <div className="tiny muted">{tirePosOf(t).label} · {dateOnly(t.installedAt)} → {dateOnly(t.removedAt)}</div>
                <div className="tiny muted">{[t.removedReason ? (TIRE_REASON[t.removedReason] ?? t.removedReason) : '',
                  t.removedTread32 !== null ? `${t.removedTread32}/32 final` : '',
                  t.removedNote].filter(Boolean).join(' · ')}</div>
              </div>
              <Badge tone={loc?.tone ?? 'muted'}>{loc?.label ?? 'Desmontada'}</Badge>
            </div>
          </div>;
        })}
        {tireHistory.length > 10 && <div className="tiny muted">Y {tireHistory.length - 10} más en el historial.</div>}
      </div>}
    </div>}

    {tireForm && <TireForm unit={unit} position={tireForm.position} editing={tireForm.editing ?? null}
      onDone={() => { tireSavedRef.current = true; void loadTires(); void loadTireStock(); }}
      onClose={() => {
        // Cancelar el formulario de un casco de bodega devuelve al modo
        // «elige posición» — el casco sigue en la mano para otro hueco.
        if (!tireSavedRef.current && tireForm.editing?.removedAt != null) setMountStock(tireForm.editing);
        tireSavedRef.current = false;
        setTireForm(null);
      }} />}
    {swapPair && <TireSwapModal unit={unit} from={swapPair.from} to={swapPair.to}
      tireFrom={activeTires.find(t => t.position === swapPair.from.key) ?? null}
      tireTo={activeTires.find(t => t.position === swapPair.to.key) ?? null}
      onDone={() => { setSwapPair(null); setRotateFrom(null); void loadTires(); }}
      onClose={() => setSwapPair(null)} />}
    {locTire && <TireLocationModal tire={locTire}
      onDone={() => { setLocTire(null); void loadTires(); void loadTireStock(); }}
      onClose={() => setLocTire(null)} />}

    {activeTab === 'reportes' && <div className="ugrid-main">
      <div className="row between multiline">
        <h3 className="card-title sm">Reportes{unitReports.length > 0 ? ` · ${unitReports.length}` : ''}</h3>
        <Btn small tone="secondary" onClick={() => setReporting(true)}>Reportar falla</Btn>
      </div>
      <ReportList reports={unitReports} unit={unit} />
      {reporting && <ReportForm unit={unit} onClose={() => setReporting(false)} />}
    </div>}

    {activeTab === 'documentos' && <div className="ugrid-main">
      {/* Checklist de cruce: un chip por documento requerido con su estado —
          falta/por verificar/vencido saltan a la vista antes de despachar. */}
      <div className="vstack">
        <div className="row between multiline">
          <h3 className="card-title sm">Para cruzar</h3>
          {requiredDocs.length > 0 && <span className="tiny muted">{crossReady} de {requiredDocs.length} vigente{crossReady === 1 ? '' : 's'}</span>}
        </div>
        {requiredDocs.length > 0
          ? <div className="xdocs">
            {requiredDocs.map(type => {
              const rd = DOC_READINESS[docReadiness(unitDocs, type)];
              return <span key={type} className="xdoc">
                {DOC_CATALOG.find(t => t.type === type)?.label ?? type}
                <Badge tone={rd.tone}>{rd.label}</Badge>
              </span>;
            })}
          </div>
          : <div className="small muted">Sin requisitos de cruce para las placas de esta unidad.</div>}
      </div>
      <div className="row between multiline">
        <h3 className="card-title sm">Documentos de la unidad</h3>
        <Btn small onClick={() => setAddingDoc(true)}>Agregar documento</Btn>
      </div>
      <DocList docs={unitDocs} />
      {addingDoc && <DocumentForm subjectType="unit" subjectId={unit.id} onClose={() => setAddingDoc(false)} />}
    </div>}

    {/* Bitácora: primero las salidas con defectos autorizadas que tocaron a
        esta unidad (quién autorizó, cuándo y a dónde iba el viaje), luego la
        línea de tiempo con cada evento auditado — carga perezosa al abrir. */}
    {activeTab === 'bitacora' && <div className="ugrid-main">
      {overrideTrips.length > 0 && <>
        <h3 className="card-title sm">Salidas con defectos autorizadas</h3>
        <div className="list">
          {overrideTrips.slice(0, 8).map(t => <div className="item" key={t.id}>
            <div className="row between multiline">
              <span className="small name">{t.driverName || 'Chofer'}</span>
              <span className="tiny muted">{dateTime(t.defectOverride?.at)}</span>
            </div>
            <div className="tiny muted">Autorizó {t.defectOverride?.byName || 'la oficina'}{t.defectOverride?.note ? ` — ${t.defectOverride.note}` : ''}</div>
            {t.stops.length > 0 && <div className="tiny muted">{t.stops.map(s => placeName(s.placeId)).join(' → ')}</div>}
          </div>)}
          {overrideTrips.length > 8 && <div className="tiny muted">Y {overrideTrips.length - 8} más.</div>}
        </div>
      </>}
      {audit === null && !auditFailed && <Skeleton rows={4} />}
      {auditFailed && <Notice tone="warning">No se pudo cargar la bitácora. <Btn small tone="ghost" onClick={() => { setAuditFailed(false); void loadAudit(); }}>Reintentar</Btn></Notice>}
      {audit !== null && !audit.length && <div className="small muted">Sin eventos todavía — los cambios de estado, lecturas, servicios y documentos aparecen aquí.</div>}
      {audit !== null && audit.length > 0 && <div className="utl">
        {audit.map(e => <AuditRow key={e.id} entry={e} unit={unit} company={company} memberName={memberName} />)}
      </div>}
    </div>}
  </Modal>;
}

/** Cambio de estado de la unidad con motivo: taller y fuera de servicio
    piden el porqué (queda en la ficha y en la bitácora); reactivar y vender
    lo llevan opcional. */
function UnitStatusModal({ unit, target, onClose }: { unit: Unit; target: UnitStatus; onClose(): void }) {
  const { token, busy, mutate, data } = usePortal();
  const company = data?.company ?? null;
  const [note, setNote] = useState('');
  const needsNote = target === 'shop' || target === 'down';
  const TITLE: Record<UnitStatus, string> = {
    active: 'Reactivar unidad', shop: 'Enviar a taller',
    down: 'Fuera de servicio', sold: 'Marcar como vendida',
  };
  const DESC: Record<UnitStatus, string> = {
    active: 'Volverá a poder asignarse a viajes.',
    shop: 'No podrá asignarse a viajes hasta que salga del taller.',
    down: 'No podrá asignarse a viajes mientras esté fuera de servicio.',
    sold: 'Saldrá de la flota disponible; si fue un error puedes reactivarla.',
  };
  const submit = () => {
    void mutate(() => api('setUnitStatus', { unitId: unit.id, status: target, note: note.trim() || undefined }, token), 'Estado de la unidad actualizado.')
      .then(ok => { if (ok) onClose(); });
  };
  return <Modal title={`${TITLE[target]} · ${unitName(unit, company)}`} onClose={onClose}>
    <p className="small muted u-m0">{DESC[target]}</p>
    <TextArea label={needsNote ? 'Motivo' : 'Motivo (opcional)'} value={note} onChange={e => setNote(e.target.value)} maxLength={500}
      placeholder={target === 'shop' ? 'Ej. Compresor dañado, servicio de 10,000 km…'
        : target === 'down' ? 'Ej. En espera de refacción, sin operador asignado…'
        : 'Ej. Venta a tercero, nota interna…'} />
    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn tone={target === 'sold' || target === 'down' ? 'danger' : 'primary'} busy={busy}
        disabled={needsNote && note.trim().length < 4} onClick={submit}>{TITLE[target]}</Btn>
    </div>
  </Modal>;
}

/** Fila del historial de servicios: tipo, fecha, lectura, taller/costo y la
    nota; Editar/Quitar son solo de oficina (el borrado corrige la marca de
    último servicio). */
function ServiceRow({ service, unit, memberName, onEdit }: { service: UnitService; unit: Unit; memberName(uid: string): string; onEdit(): void }) {
  const { token, busy, mutate, data, session } = usePortal();
  const isOffice = data?.company?.role === 'office' || session.role === 'admin';
  const cmp = service.componentId ? (unit.components ?? []).find(c => c.id === service.componentId) : null;
  const meterSuffix = METER_UNIT[cmp ? cmp.meterKind : unit.meterKind];
  const remove = () => {
    if (!confirm('¿Borrar este registro de servicio?')) return;
    void mutate(() => api('deleteUnitService', { serviceId: service.id }, token), 'Servicio eliminado.');
  };
  return <div className="item">
    <div className="row between multiline">
      <div className="grow">
        <div className="title sm">{SERVICE_KIND[service.kind] ?? 'Servicio'}{cmp ? ` · ${cmp.label || 'Componente'}` : ''}</div>
        <div className="tiny muted">
          {[dateOnly(service.at), service.meterValue !== null ? `${service.meterValue.toLocaleString('es-MX')} ${meterSuffix}` : '',
            service.vendor, service.cost !== null ? `$${service.cost.toLocaleString('es-MX')}` : '',
            memberName(service.createdBy)].filter(Boolean).join(' · ')}
        </div>
        {service.note && <div className="tiny muted">{service.note}</div>}
        {(service.nextMeter !== null || service.nextAt) && <div className="tiny u-warn">
          Próximo: {[service.nextMeter !== null ? `${service.nextMeter.toLocaleString('es-MX')} ${meterSuffix}` : '', service.nextAt ? dateOnly(service.nextAt) : ''].filter(Boolean).join(' o ')}
        </div>}
      </div>
      {isOffice && <div className="hstack">
        <Btn small tone="ghost" onClick={onEdit}>Editar</Btn>
        <Btn small tone="ghost" busy={busy} onClick={remove}>Quitar</Btn>
      </div>}
    </div>
  </div>;
}

/** Fila del historial de inspecciones: tipo (pre/post-viaje), fecha, saldo
    de defectos y quién la firmó; Imprimir abre la hoja DVIR y Quitar es
    solo de oficina. */
function InspectionRow({ inspection, onPrint }: { inspection: UnitInspection; onPrint(): void }) {
  const { token, busy, mutate, data, session } = usePortal();
  const isOffice = data?.company?.role === 'office' || session.role === 'admin';
  const report = inspection.reportId ? data?.companyUnitReports?.find(r => r.id === inspection.reportId) : null;
  const remove = () => {
    if (!confirm('¿Borrar esta inspección?')) return;
    void mutate(() => api('deleteUnitInspection', { inspectionId: inspection.id }, token), 'Inspección eliminada.');
  };
  return <div className="item">
    <div className="row between multiline">
      <span className="small name">{INSPECTION_KIND_LABEL[inspection.kind] ?? inspection.kind}</span>
      <div className="hstack">
        <Badge tone={inspection.outcome === 'ok' ? 'ok' : 'bad'}>{inspection.outcome === 'ok' ? 'Sin defectos' : `${inspection.defects.length} defecto${inspection.defects.length === 1 ? '' : 's'}`}</Badge>
        <Btn small tone="ghost" onClick={onPrint}>Imprimir</Btn>
        {isOffice && <Btn small tone="ghost" busy={busy} onClick={remove}>Quitar</Btn>}
      </div>
    </div>
    <div className="tiny muted">{[dateTime(inspection.createdAt), inspection.createdByName || 'Miembro', inspection.reportId ? `reporte ${REPORT_STATUS[report?.status ?? 'open']?.label?.toLowerCase() ?? 'ligado'}` : '', inspection.reviewedPrevious ? 'revisó reporte previo' : ''].filter(Boolean).join(' · ')}</div>
  </div>;
}

/** Registro/edición de servicio: tipo, fecha, lectura del contador en ese
    momento, taller y costo opcionales; el «próximo» fija el aviso (por
    contador o fecha). Actualiza la marca de último servicio de la
    unidad/componente. Al editar, la pieza queda fija (el servicio exige
    que coincida con la del registro original). */
function ServiceForm({ unit, editing, onClose }: { unit: Unit; editing?: UnitService | null; onClose(): void }) {
  const { token, busy, error, mutate, dismiss } = usePortal();
  const components = unit.components ?? [];
  const [kind, setKind] = useState<UnitServiceKind>(editing?.kind ?? 'oil');
  const [componentId, setComponentId] = useState(editing?.componentId ?? '');
  const [at, setAt] = useState(() => editing?.at ? editing.at.slice(0, 10) : new Date().toISOString().slice(0, 10));
  const [vendor, setVendor] = useState(editing?.vendor ?? '');
  const [cost, setCost] = useState(editing?.cost != null ? String(editing.cost) : '');
  const [note, setNote] = useState(editing?.note ?? '');
  const [nextMeter, setNextMeter] = useState(editing?.nextMeter != null ? String(editing.nextMeter) : '');
  const [nextAt, setNextAt] = useState(editing?.nextAt ? editing.nextAt.slice(0, 10) : '');

  const cmp = componentId ? components.find(c => c.id === componentId) : null;
  const meterKind = cmp ? cmp.meterKind : unit.meterKind;
  const meterUnit = METER_UNIT[meterKind];
  const current = (cmp ? cmp.meter : unit.meter)?.value ?? null;
  // km/mi a elección: meterIn.value ya llega en la unidad propia del
  // contador. Al editar no se exige ≥ lectura actual: el contador pudo
  // avanzar después.
  const meterIn = useMeterInput(meterKind, editing?.meterValue != null ? String(editing.meterValue) : '');
  const meterValid = meterIn.valid && (meterIn.value === null || !!editing || current === null || meterIn.value >= current);
  // Number() de texto libre puede dar NaN — serializa a null y ensucia el
  // registro; misma validación que el costo del cierre de reporte.
  const costNum = cost.trim() ? Number(cost) : null;
  const costValid = costNum === null || (Number.isFinite(costNum) && costNum >= 0);
  const nextMeterNum = nextMeter.trim() ? Number(nextMeter) : null;
  const nextMeterValid = nextMeterNum === null || (Number.isFinite(nextMeterNum) && nextMeterNum > 0);

  const submit = async () => {
    const input: UnitServiceInput = {
      unitId: unit.id, componentId: editing ? editing.componentId : componentId || null, kind,
      at: at || null,
      meterValue: meterIn.value,
      vendor: vendor.trim(), note: note.trim(),
      cost: costNum,
      nextMeter: nextMeterNum,
      nextAt: nextAt || null,
    };
    const ok = await mutate(() => editing
      ? api('updateUnitService', { serviceId: editing.id, input }, token)
      : api('addUnitService', { input }, token), editing ? 'Servicio actualizado.' : 'Servicio registrado.');
    if (ok) onClose();
  };

  return <Modal title={`${editing ? 'Editar' : 'Registrar'} servicio · ${unitName(unit)}`} onClose={onClose} wide>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <div className="form-grid">
      <Select label="Tipo de servicio" value={kind} onChange={e => setKind(e.target.value as UnitServiceKind)} autoFocus>
        {(Object.keys(SERVICE_KIND) as UnitServiceKind[]).map(k => <option key={k} value={k}>{SERVICE_KIND[k]}</option>)}
      </Select>
      {components.length > 0 && <Select label="Pieza" value={componentId} onChange={e => setComponentId(e.target.value)} disabled={!!editing}
        hint={editing ? 'La pieza no se puede cambiar al editar.' : 'Si el trabajo fue al reefer u otro componente, elígelo — lleva su propio contador.'}>
        <option value="">La unidad completa</option>
        {components.map(c => <option key={c.id} value={c.id}>{c.label || 'Componente'}{c.brand ? ` (${[c.brand, c.model].filter(Boolean).join(' ')})` : ''}</option>)}
      </Select>}
      <Field label="Fecha del servicio" type="date" value={at} onChange={e => setAt(e.target.value)} />
      {meterKind !== 'none' && <div className="field">
        <label htmlFor="svc-meter">Lectura al servir ({meterIn.label || meterUnit || 'contador'})</label>
        <div className="row">
          <input className="input num" id="svc-meter" type="number" min={0} inputMode="numeric"
            value={meterIn.text} onChange={e => meterIn.setText(e.target.value)}
            placeholder={current !== null ? `≥ ${meterIn.toCapture(current).toLocaleString('es-MX')} ${meterIn.label}` : 'Lectura del odómetro'} />
          {meterIn.seg}
        </div>
        <span className="hint">{[current !== null ? `Última registrada: ${current.toLocaleString('es-MX')} ${meterUnit}.` : '', meterIn.hint].filter(Boolean).join(' ') || 'Opcional.'}</span>
      </div>}
      <Field label="Taller o proveedor" value={vendor} onChange={e => setVendor(e.target.value)} maxLength={80} placeholder="Ej. Taller El Güero" />
      <Field label="Costo" type="number" min={0} inputMode="decimal" value={cost} onChange={e => setCost(e.target.value)} placeholder="Opcional" />
      <Field label={`Próximo servicio a los N ${meterUnit || 'km/h'}`} type="number" min={1} inputMode="numeric"
        value={nextMeter} onChange={e => setNextMeter(e.target.value)} placeholder="Opcional — avisa al llegar" />
      <Field label="O próximo servicio en fecha" type="date" value={nextAt} onChange={e => setNextAt(e.target.value)} />
    </div>
    <TextArea label="Nota" value={note} onChange={e => setNote(e.target.value)} maxLength={500}
      placeholder="Ej. Aceite sintético + filtros, quedó apretada la banda…" />
    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} disabled={!at || !meterValid || !costValid || !nextMeterValid} onClick={() => void submit()}>{editing ? 'Guardar cambios' : 'Guardar servicio'}</Btn>
    </div>
  </Modal>;
}

/** Inspección físico-mecánica (walkaround) pre o post-viaje: cada punto se
    marca bien/defecto/N-A y los defectos llevan su nota; con defectos se
    elige gravedad (el servicio puede abrir reporte). La firma es la
    sesión conectada — no hay campo de quién. */
function InspectionForm({ unit, kind, previousDefects = [], onClose }: { unit: Unit; kind: UnitInspectionKind; previousDefects?: { item: string }[]; onClose(): void }) {
  const { token, busy, error, mutate, dismiss, data } = usePortal();
  const company = data?.company ?? null;
  const groups = useMemo(() => inspectionGroupsFor(unit), [unit]);
  const total = groups.reduce((n, g) => n + g.items.length, 0);
  // Puntos que fallaron en la última inspección: se destacan para que el
  // inspector los revise de nuevo con cuidado (re-verificación §396.11).
  const prevDefectKeys = useMemo(() => new Set(previousDefects.map(d => d.item)), [previousDefects]);
  const [items, setItems] = useState<Record<string, InspectionItemStatus>>(() =>
    Object.fromEntries(groups.flatMap(g => g.items.map(i => [i.key, 'ok' as const]))));
  const [defectNotes, setDefectNotes] = useState<Record<string, string>>({});
  const [severity, setSeverity] = useState<UnitReportSeverity>('medium');
  const [notes, setNotes] = useState('');
  const meterIn = useMeterInput(unit.meterKind, unit.meter ? String(unit.meter.value) : '');
  const [reviewedPrevious, setReviewedPrevious] = useState(false);
  // Una foto por ítem defectuoso: viaja embebida en `photos` del input.
  const [defectPhotos, setDefectPhotos] = useState<Record<string, { contentType: string; dataBase64: string }>>({});
  const [photoError, setPhotoError] = useState('');
  const photoInputRef = useRef<HTMLInputElement>(null);
  const photoForRef = useRef('');

  const mark = (key: string, status: InspectionItemStatus) => setItems(prev => ({ ...prev, [key]: status }));
  const defectKeys = groups.flatMap(g => g.items).filter(i => items[i.key] === 'defect').map(i => i.key);
  const meterUnit = METER_UNIT[unit.meterKind];
  const meterValid = meterIn.valid;
  const SEVERITY_LABEL: Record<UnitReportSeverity, string> = { low: 'Leve', medium: 'Media', high: 'Grave' };

  const pickPhoto = (itemKey: string) => { photoForRef.current = itemKey; photoInputRef.current?.click(); };
  const onDefectPhoto = async (file: File) => {
    const key = photoForRef.current;
    if (!key) return;
    setPhotoError('');
    // photoFromFile reduce a JPEG si pasa del límite de 4.5 MB del servicio.
    const photo = await photoFromFile(file).catch(() => null);
    if (photo) setDefectPhotos(prev => ({ ...prev, [key]: photo }));
    else setPhotoError('No se pudo leer la foto.');
  };

  const submit = async () => {
    const photoList = defectKeys.filter(k => defectPhotos[k]).map(k => ({ item: k, photo: defectPhotos[k] }));
    const input: UnitInspectionInput = {
      unitId: unit.id, kind, items,
      defects: defectKeys.map(k => ({ item: k, note: (defectNotes[k] ?? '').trim() })),
      photos: photoList.length ? photoList : undefined,
      severity: defectKeys.length ? severity : undefined,
      notes: notes.trim() || undefined,
      meter: meterIn.value,
      reviewedPrevious: kind === 'pretrip' ? reviewedPrevious : undefined,
    };
    const ok = await mutate(() => api('addUnitInspection', { input }, token, 60000), 'Inspección registrada.');
    if (ok) onClose();
  };

  return <Modal title={`Inspección ${kind === 'pretrip' ? 'pre-viaje' : 'post-viaje'} · ${unitName(unit, company)}`} onClose={onClose} wide>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <p className="tiny muted u-m0">Recorre la unidad y marca cada punto. La firma digital eres tú: se guarda con tu nombre y hora.</p>

    {unit.meterKind !== 'none' && <div className="field">
      <label htmlFor="insp-meter">Lectura del contador ({meterIn.label || meterUnit})</label>
      <div className="row">
        <input className="input num wide" id="insp-meter" type="number" min={0} inputMode="numeric"
          value={meterIn.text} onChange={e => meterIn.setText(e.target.value)}
          placeholder={`Lectura en ${meterIn.label || 'contador'}`} />
        {meterIn.seg}
      </div>
      <span className="hint">Opcional — queda como lectura del odómetro al momento de la inspección.{meterIn.hint ? ` ${meterIn.hint}` : ''}</span>
    </div>}

    {kind === 'pretrip' && <label className="insp-review">
      <input type="checkbox" checked={reviewedPrevious} onChange={e => setReviewedPrevious(e.target.checked)} />
      <span>Revisé el último reporte de inspección y la reparación de sus defectos.</span>
    </label>}

    {prevDefectKeys.size > 0 && <Notice tone="warning">La última inspección marcó {prevDefectKeys.size} defecto{prevDefectKeys.size === 1 ? '' : 's'} — aparecen señalados para que los revises de nuevo.</Notice>}

    {groups.map(g => <section key={g.key}>
      <h3 className="card-title sm">{g.label}</h3>
      {g.items.map(item => {
        const status = items[item.key] ?? 'ok';
        return <div className="insp-item" key={item.key}>
          <div className="insp-row">
            <span className="insp-label">{item.label}{prevDefectKeys.has(item.key) && <em className="insp-prev">revisar</em>}</span>
            <div className="insp-tri" role="group" aria-label={item.label}>
              <button type="button" className={`insp-btn${status === 'ok' ? ' on' : ''}`} aria-pressed={status === 'ok'}
                title="En orden" onClick={() => mark(item.key, 'ok')}>✓</button>
              <button type="button" className={`insp-btn${status === 'defect' ? ' bad' : ''}`} aria-pressed={status === 'defect'}
                title="Defecto" onClick={() => mark(item.key, 'defect')}>✕</button>
              <button type="button" className={`insp-btn${status === 'na' ? ' on' : ''}`} aria-pressed={status === 'na'}
                title="No aplica" onClick={() => mark(item.key, 'na')}>—</button>
            </div>
          </div>
          {status === 'defect' && <div className="insp-defect">
            <input className="input insp-note" value={defectNotes[item.key] ?? ''} maxLength={300}
              placeholder="¿Qué viste?" aria-label={`Nota del defecto en ${item.label}`}
              onChange={e => setDefectNotes(prev => ({ ...prev, [item.key]: e.target.value }))} />
            <button type="button" className={`btn small ${defectPhotos[item.key] ? 'secondary' : 'ghost'}`}
              aria-label={`Foto del defecto en ${item.label}`} onClick={() => pickPhoto(item.key)}>
              <Icon name="camera" size={14} />{defectPhotos[item.key] ? 'Foto ✓' : 'Foto'}
            </button>
          </div>}
        </div>;
      })}
    </section>)}

    {defectKeys.length > 0 && <div className="field">
      <label id="insp-sev">Gravedad de los defectos</label>
      <div className="seg" role="group" aria-labelledby="insp-sev">
        {(['low', 'medium', 'high'] as UnitReportSeverity[]).map(s => <button key={s} type="button"
          className={`seg-btn${severity === s ? ' on' : ''}`} aria-pressed={severity === s}
          onClick={() => setSeverity(s)}>{SEVERITY_LABEL[s]}</button>)}
      </div>
      <span className="hint">{REPORT_SEVERITY[severity].label} — con defectos graves la unidad no debería salir.</span>
    </div>}

    <TextArea label="Notas generales" value={notes} onChange={e => setNotes(e.target.value)} maxLength={1000}
      placeholder="Ej. Vibra el volante a 90 km/h, revisar en el próximo servicio…" />

    {/* Un solo input de archivo oculto para todas las fotos de defecto:
        photoForRef recuerda qué ítem la pidió. */}
    <input ref={photoInputRef} type="file" accept="image/*" capture="environment" hidden
      onChange={e => { const f = e.target.files?.[0]; if (f) void onDefectPhoto(f); e.target.value = ''; }} />
    {photoError && <span className="tiny u-bad">{photoError}</span>}

    <div className="actions end modal-actions">
      <span className="tiny muted" style={{ marginRight: 'auto' }}>{defectKeys.length
        ? `${defectKeys.length} de ${total} ítems marcados defecto`
        : `${total} ítems revisados — sin defectos`}</span>
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} disabled={!meterValid} onClick={() => void submit()}>Guardar inspección</Btn>
    </div>
  </Modal>;
}

// La bitácora (etiquetas, nota traducida y la fila `.utl-item`) vive en
// ./Audit — la misma línea de tiempo alimenta la mini-bitácora del Tablero
// (listAudit) con los eventos de toda la empresa.

/** Escape cierra la hoja imprimible: se escucha en fase de captura con
    stopPropagation para ganarle al keydown del Modal que la contiene —
    así solo baja la hoja y la ficha de la unidad sigue abierta. */
function usePrintClose(onClose: () => void) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
}

/** Expediente imprimible de la unidad («truck packet»): datos, placas,
    contador, operador y documentos vigentes en una hoja blanca para
    imprimir o guardar como PDF desde el navegador. */
function UnitPacket({ unit, docs, members, onClose }: { unit: Unit; docs: UnitDocument[]; members: Session[]; onClose(): void }) {
  const { token, data, authFail } = usePortal();
  const company = data?.company ?? null;
  const cid = data?.company?.id ?? '';
  const [photo, setPhoto] = useState('');
  useEffect(() => {
    let alive = true;
    const first = unit.photoIds?.[0];
    if (!first || !cid) return;
    void mediaBlobUrl(`company/${cid}/unit/${unit.id}/${first}`, token)
      .then(url => { if (alive) setPhoto(url); }).catch(e => { authFail(e); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit.id, unit.photoIds, cid, token]);
  usePrintClose(onClose);

  const holder = members.find(m => m.defaultTractorId === unit.id || m.defaultTrailerId === unit.id);
  const current = unit.meter;
  const meterUnit = METER_UNIT[unit.meterKind];
  const liveDocs = docs.filter(d => !d.supersededBy)
    .sort((a, b) => String(a.expiresAt ?? '9999').localeCompare(String(b.expiresAt ?? '9999')));
  // Fecha y saldo de la última inspección (el conteo sale del registro si
  // todavía está en el snapshot).
  const lastInspection = (data?.companyUnitInspections ?? []).find(i => i.unitId === unit.id);
  const inspText = !unit.lastInspectionAt ? 'Sin inspecciones'
    : `${dateOnly(unit.lastInspectionAt)} — ${unit.lastInspectionOutcome === 'defects'
        ? (lastInspection ? `${lastInspection.defects.length} defecto${lastInspection.defects.length === 1 ? '' : 's'}` : 'Con defectos')
        : 'Sin defectos'}`;

  return <div className="print-wrap" role="dialog" aria-modal="true" aria-label="Expediente de la unidad">
    <button type="button" className="pd-close" onClick={onClose} aria-label="Cerrar" title="Cerrar (Esc)">✕</button>
    <div className="print-doc">
      <header className="pd-head">
        <div>
          <div className="pd-company">{company?.name ?? 'Empresa'}</div>
          <h1 className="pd-title">{unitName(unit, company)} — expediente de unidad</h1>
          <div className="pd-sub">{[unitKindLabel(unit, company), UNIT_CATEGORY[unitCategoryOf(unit, company)].label, `Generado ${dateTime(new Date().toISOString())}`].join(' · ')}</div>
        </div>
        {photo && <img className="pd-photo" src={photo} alt="" />}
      </header>

      <section className="pd-sec">
        <h2>Datos</h2>
        <dl className="pd-kv">
          <dt>Número interno</dt><dd>{unit.internalNumber || '—'}</dd>
          <dt>Marca / Modelo / Año</dt><dd>{[unit.brand, unit.model, unit.year].filter(Boolean).join(' ') || '—'}</dd>
          <dt>VIN</dt><dd className="mono">{unit.vin || '—'}</dd>
          {unit.plates.map((p, i) => <Fragment key={i}><dt>Placa {p.country}</dt><dd>{p.number}</dd></Fragment>)}
          <dt>Propiedad</dt><dd>{UNIT_OWNER[unit.owner]}</dd>
          <dt>Estado</dt><dd>{UNIT_STATUS[unit.status].label}</dd>
          <dt>Última inspección</dt><dd>{inspText}</dd>
          {unit.meterKind !== 'none' && <><dt>Contador</dt><dd>{current ? `${current.value.toLocaleString('es-MX')} ${meterUnit} (${dateOnly(current.at)})` : `Sin lectura (${UNIT_METER[unit.meterKind].toLowerCase()})`}</dd></>}
          {holder && <><dt>Operador habitual</dt><dd>{holder.displayName || holder.email}</dd></>}
        </dl>
      </section>

      {(unit.components ?? []).length > 0 && <section className="pd-sec">
        <h2>Componentes</h2>
        <dl className="pd-kv">
          {(unit.components ?? []).map(c => <Fragment key={c.id}>
            <dt>{c.label || 'Componente'}</dt>
            <dd>{[c.brand, c.model].filter(Boolean).join(' ') || '—'}{c.serial ? ` · serie ${c.serial}` : ''}{c.meter ? ` · ${c.meter.value.toLocaleString('es-MX')} ${METER_UNIT[c.meterKind]}` : ''}</dd>
          </Fragment>)}
        </dl>
      </section>}

      <section className="pd-sec">
        <h2>Documentos vigentes</h2>
        {!liveDocs.length ? <p className="pd-empty">Sin documentos registrados.</p> : <table className="pd-table">
          <thead><tr><th>Documento</th><th>Número</th><th>País</th><th>Vence</th><th>Estado</th></tr></thead>
          <tbody>{liveDocs.map(d => <tr key={d.id}>
            <td>{d.label || d.type}</td><td>{d.number || '—'}</td><td>{d.country ?? '—'}</td>
            <td>{d.expiresAt ? dateOnly(d.expiresAt) : '—'}</td><td>{d.status === 'verified' ? 'Verificado' : d.status === 'rejected' ? 'Rechazado' : 'En revisión'}</td>
          </tr>)}</tbody>
        </table>}
      </section>

      <footer className="pd-foot">Generado desde Cargados.mx · {company?.name ?? ''}</footer>

      <div className="pd-actions">
        <Btn tone="ghost" onClick={onClose}>Cerrar</Btn>
        <Btn onClick={() => window.print()}>Imprimir / Guardar PDF</Btn>
      </div>
    </div>
  </div>;
}

/** Hoja DVIR imprimible de la inspección físico-mecánica: encabezado con la
    unidad, walkaround completo con la marca de cada ítem (✓ / ✗ / —),
    defectos con sus notas y líneas de firma — mismo patrón que UnitPacket. */
function InspectionPacket({ inspection, unit, onClose }: { inspection: UnitInspection; unit: Unit; onClose(): void }) {
  const { data } = usePortal();
  const company = data?.company ?? null;
  const groups = useMemo(() => inspectionGroupsFor(unit), [unit]);
  const defects = inspection.defects ?? [];
  const meterUnit = METER_UNIT[unit.meterKind];
  const MARK: Record<InspectionItemStatus, string> = { ok: '✓', defect: '✗', na: '—' };
  // Si la inspección va ligada a un viaje con salida autorizada, el waiver
  // queda en el papel: el chofer lo enseña en báscula.
  const trip = inspection.tripId ? (data?.companyTrips ?? []).find(t => t.id === inspection.tripId) : undefined;
  const waiver = trip?.defectOverride ?? null;
  usePrintClose(onClose);

  return <div className="print-wrap" role="dialog" aria-modal="true" aria-label="Hoja de inspección">
    <button type="button" className="pd-close" onClick={onClose} aria-label="Cerrar" title="Cerrar (Esc)">✕</button>
    <div className="print-doc">
      <header className="pd-head">
        <div>
          <div className="pd-company">{company?.name ?? 'Empresa'}</div>
          <h1 className="pd-title">Inspección físico-mecánica — {INSPECTION_KIND_LABEL[inspection.kind] ?? inspection.kind}</h1>
          <div className="pd-sub">{[unitName(unit, company), dateTime(inspection.createdAt)].join(' · ')}</div>
        </div>
      </header>

      <section className="pd-sec">
        <h2>Unidad y registro</h2>
        <dl className="pd-kv">
          <dt>Número interno</dt><dd>{unit.internalNumber || '—'}</dd>
          <dt>Marca / Modelo / Año</dt><dd>{[unit.brand, unit.model, unit.year].filter(Boolean).join(' ') || '—'}</dd>
          <dt>VIN</dt><dd className="mono">{unit.vin || '—'}</dd>
          <dt>Placas</dt><dd>{platesText(unit) || '—'}</dd>
          <dt>Inspeccionó</dt><dd>{inspection.createdByName || 'Miembro'}</dd>
          <dt>Fecha y hora</dt><dd>{dateTime(inspection.createdAt)}</dd>
          {unit.meterKind !== 'none' && <><dt>Contador</dt>
            <dd>{inspection.meter !== null ? `${inspection.meter.toLocaleString('es-MX')} ${meterUnit}` : `Sin lectura (${UNIT_METER[unit.meterKind].toLowerCase()})`}</dd></>}
          {inspection.kind === 'pretrip' && <><dt>Revisión previa</dt>
            <dd>{inspection.reviewedPrevious ? 'Revisó el reporte anterior y la reparación de sus defectos' : 'No marcada'}</dd></>}
          <dt>Resultado</dt><dd>{inspection.outcome === 'ok' ? 'Sin defectos' : `${defects.length} defecto${defects.length === 1 ? '' : 's'}`}</dd>
          {waiver && <><dt>Salida con defectos</dt>
            <dd>Autorizada por {waiver.byName || 'la oficina'}{waiver.at ? ` · ${dateTime(waiver.at)}` : ''}{waiver.note ? ` — ${waiver.note}` : ''}</dd></>}
        </dl>
      </section>

      {groups.map(g => <section className="pd-sec" key={g.key}>
        <h2>{g.label}</h2>
        <table className="pd-table">
          <tbody>{g.items.map(item => {
            const st = inspection.items?.[item.key];
            return <tr key={item.key}><td>{item.label}</td><td className="pd-mark">{st ? MARK[st] : '·'}</td></tr>;
          })}</tbody>
        </table>
      </section>)}

      <section className="pd-sec">
        <h2>Defectos{defects.length ? ` · ${defects.length}` : ''}</h2>
        {!defects.length ? <p className="pd-empty">Sin defectos — la unidad salió en orden.</p> : <>
          <table className="pd-table">
            <thead><tr><th>Punto</th><th>Detalle</th><th>Foto</th></tr></thead>
            <tbody>{defects.map((d, i) => <tr key={i}>
              <td>{INSPECTION_ITEM_LABEL[d.item] ?? d.item}</td>
              <td>{d.note || '—'}</td>
              <td>{d.photoId ? <DefectThumb inspectionId={inspection.id} photoId={d.photoId} /> : '—'}</td>
            </tr>)}</tbody>
          </table>
          <p className="pd-empty">Gravedad reportada: {REPORT_SEVERITY[inspection.severity]?.label ?? inspection.severity}.</p>
        </>}
      </section>

      {inspection.notes && <section className="pd-sec">
        <h2>Notas</h2>
        <p>{inspection.notes}</p>
      </section>}

      <div className="pd-sign">
        <div className="pd-sign-box"><span className="pd-sign-line" />Firma del inspector</div>
        <div className="pd-sign-box"><span className="pd-sign-line" />Recibió (nombre y firma)</div>
        <div className="pd-sign-box"><span className="pd-sign-line" />Certificación de reparación</div>
      </div>

      <footer className="pd-foot">Generado desde Cargados.mx · {company?.name ?? ''}</footer>

      <div className="pd-actions">
        <Btn tone="ghost" onClick={onClose}>Cerrar</Btn>
        <Btn onClick={() => window.print()}>Imprimir / Guardar PDF</Btn>
      </div>
    </div>
  </div>;
}

/** Miniatura de la foto de un defecto en la hoja DVIR (blob con Bearer,
    carga perezosa como UnitCover); también sale en la impresión. */
function DefectThumb({ inspectionId, photoId }: { inspectionId: string; photoId: string }) {
  const { token, data, authFail } = usePortal();
  const cid = data?.company?.id ?? '';
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!cid) return;
    let alive = true;
    void mediaBlobUrl(`company/${cid}/insp/${inspectionId}/${photoId}`, token)
      .then(u => { if (alive) setUrl(u); }).catch(e => { authFail(e); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, inspectionId, photoId, token]);
  return url ? <img className="pd-thumb" src={url} alt="Foto del defecto" /> : <span className="tiny muted">…</span>;
}

/** Portada de la tarjeta: la primera foto de la unidad si existe (blob con
    Bearer, carga perezosa); sin foto queda el icono del tipo. */
function UnitCover({ unit }: { unit: Unit }) {
  const { token, data, authFail } = usePortal();
  const cid = data?.company?.id ?? '';
  const photoId = unit.photoIds?.[0] ?? '';
  const [url, setUrl] = useState('');
  useEffect(() => {
    let alive = true;
    if (!photoId || !cid) return;
    void mediaBlobUrl(`company/${cid}/unit/${unit.id}/${photoId}`, token)
      .then(u => { if (alive) setUrl(u); })
      .catch(e => { authFail(e); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit.id, photoId, cid, token]);
  if (url) return <span className="uicon thumb"><img src={url} alt="" /></span>;
  return <span className={`uicon${unit.status !== 'active' ? ' off' : ''}`}><Icon name={kindIcon(unit)} size={20} /></span>;
}

/** Galería de la unidad: foto principal + miniaturas, subir/quitar. Las fotos
    se sirven como blob con Bearer (media:company/<cid>/unit/<unitId>/<ph-…>);
    la primera del arreglo es la portada. */
function UnitGallery({ unit }: { unit: Unit }) {
  const { token, busy, mutate, data, authFail } = usePortal();
  const cid = data?.company?.id ?? '';
  const photoIds = unit.photoIds ?? [];
  const [blobs, setBlobs] = useState<Record<string, string>>({});
  const [active, setActive] = useState(0);
  const [photoError, setPhotoError] = useState('');
  // La compresión corre antes de mutate(): sin bandera propia, elegir otra
  // foto con la subida en vuelo perdía el archivo en silencio.
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const refOf = (pid: string) => `company/${cid}/unit/${unit.id}/${pid}`;
  useEffect(() => {
    let alive = true;
    const missing = photoIds.filter(pid => !blobs[pid]);
    if (!missing.length || !cid) return;
    void Promise.all(missing.map(pid => mediaBlobUrl(refOf(pid), token).then(url => [pid, url] as const).catch(e => { authFail(e); return null; })))
      .then(rows => { if (alive) setBlobs(prev => ({ ...prev, ...Object.fromEntries(rows.filter(Boolean) as [string, string][]) })); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit.id, photoIds.join(','), cid, token]);

  const activeId = photoIds[Math.min(active, photoIds.length - 1)];
  const activeUrl = activeId ? blobs[activeId] : '';

  const upload = async (file: File) => {
    if (uploading || busy) return;
    setUploading(true);
    setPhotoError('');
    try {
      // photoFromFile reduce a JPEG si la foto pasa del límite de 4.5 MB del
      // servicio — las fotos de celular llegan así sin error. Los errores de la
      // API se ven en el aviso del modal (contexto).
      const photo = await photoFromFile(file).catch(() => null);
      if (!photo) { setPhotoError('No se pudo leer la foto.'); return; }
      void mutate(() => api('addUnitPhoto', { unitId: unit.id, photo }, token, 60000), 'Foto subida.');
    } finally { setUploading(false); }
  };
  const remove = () => {
    if (!activeId || !confirm('¿Quitar esta foto de la unidad?')) return;
    void mutate(() => api('removeUnitPhoto', { unitId: unit.id, photoId: activeId }, token), 'Foto eliminada.')
      .then(ok => { if (ok) { setBlobs(prev => { const next = { ...prev }; delete next[activeId]; return next; }); setActive(0); } });
  };

  return <div className="ug-box">
    <button type="button" className={`ug-photo${activeUrl ? ' has' : ''}`} aria-label={activeUrl ? 'Ampliar foto' : 'La unidad no tiene foto'}
      onClick={() => { if (activeUrl) window.open(activeUrl, '_blank', 'noopener'); }}>
      {activeUrl ? <img src={activeUrl} alt={`Foto de ${unitName(unit, data?.company)}`} />
        : <span className="ug-empty"><Icon name="camera" size={26} /><span className="tiny">Sin foto</span></span>}
    </button>
    {photoIds.length > 1 && <div className="ug-thumbs">
      {photoIds.map((pid, i) => <button key={pid} type="button" className={`ug-thumb${pid === activeId ? ' on' : ''}`} onClick={() => setActive(i)} aria-label={`Foto ${i + 1}`}>
        {blobs[pid] ? <img src={blobs[pid]} alt="" /> : <span className="ug-thumb-load" />}
      </button>)}
    </div>}
    <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" hidden
      onChange={e => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
    <div className="row">
      <Btn small tone="secondary" busy={busy || uploading} disabled={photoIds.length >= 8} onClick={() => fileRef.current?.click()}>
        {photoIds.length ? 'Subir otra' : 'Subir foto'}</Btn>
      {photoIds.length > 0 && <Btn small tone="ghost" busy={busy} onClick={remove}>Quitar</Btn>}
    </div>
    {photoError && <span className="tiny u-bad">{photoError}</span>}
  </div>;
}

/** Reportes de falla de la unidad: abiertos/en taller primero, resueltos al
    final. La oficina los mueve de estado; la foto del reporte se abre como
    blob (media:company/<cid>/report/<reportId>/<ph-…>). */
function ReportList({ reports, unit }: { reports: UnitReport[]; unit: Unit }) {
  const { token, busy, mutate, data, session, authFail } = usePortal();
  const cid = data?.company?.id ?? '';
  const isOffice = data?.company?.role === 'office' || session.role === 'admin';
  const ordered = [...reports].sort((a, b) => {
    const open = (r: UnitReport) => r.status === 'resolved' ? 1 : 0;
    return open(a) - open(b) || String(b.createdAt).localeCompare(String(a.createdAt));
  });
  const shown = ordered.slice(0, 12);
  const [opening, setOpening] = useState('');

  const openPhoto = async (r: UnitReport) => {
    if (!r.photoId || !cid) return;
    setOpening(r.id);
    try { window.open(await mediaBlobUrl(`company/${cid}/report/${r.id}/${r.photoId}`, token), '_blank', 'noopener'); }
    catch (e) { authFail(e); /* el aviso de error global basta */ }
    finally { setOpening(''); }
  };
  const [resolving, setResolving] = useState<UnitReport | null>(null);
  const setStatus = (r: UnitReport, status: UnitReportStatus) => {
    // Resolver abre su propio modal (nota + taller/costo opcionales); los
    // demás estados van directo.
    if (status === 'resolved') { setResolving(r); return; }
    void mutate(() => api('setUnitReportStatus', { reportId: r.id, status }, token),
      status === 'inShop' ? 'Reporte marcado en taller.' : 'Reporte reabierto.');
  };

  if (!ordered.length) return <div className="small muted">Sin reportes — si el chofer levanta una falla desde la app aparece aquí.</div>;
  const cat = unitCategoryOf(unit, data?.company);
  return <>
    <div className="list">
      {shown.map(r => {
        const sev = REPORT_SEVERITY[r.severity] ?? REPORT_SEVERITY.medium;
        const sta = REPORT_STATUS[r.status] ?? REPORT_STATUS.open;
        const cmp = r.componentId ? (unit.components ?? []).find(c => c.id === r.componentId) : null;
        return <div className="item" key={r.id}>
          <div className="row between multiline">
            <div className="grow">
              <div className="title sm">{r.description}</div>
              <div className="tiny muted">{r.reportedByName || 'Miembro'} · {timeAgo(r.createdAt)}{cmp ? ` · ${cmp.label || 'Componente'}` : ''}{r.tirePosition ? ` · Llanta: ${tirePositionLabel(unit.kind, cat, r.tirePosition)}` : ''}</div>
              {r.status === 'resolved' && r.resolveNote && <div className="tiny muted">Cierre: {r.resolveNote}</div>}
              {r.status === 'resolved' && (r.repairedBy || r.repairCost != null) && <div className="tiny muted">
                Reparó: {[r.repairedBy, r.repairCost != null ? `$${r.repairCost.toLocaleString('es-MX')}` : ''].filter(Boolean).join(' · ')}
              </div>}
            </div>
            <div className="hstack">
              <Badge tone={sev.tone}>{sev.label}</Badge>
              <Badge tone={sta.tone}>{sta.label}</Badge>
            </div>
          </div>
          <div className="actions">
            {r.photoId && <Btn small tone="ghost" busy={opening === r.id} onClick={() => void openPhoto(r)}>Ver foto</Btn>}
            {isOffice && r.status !== 'resolved' && <>
              {r.status === 'open' && <Btn small tone="ghost" busy={busy} onClick={() => setStatus(r, 'inShop')}>En taller</Btn>}
              <Btn small tone="secondary" busy={busy} onClick={() => setStatus(r, 'resolved')}>Resolver</Btn>
            </>}
            {isOffice && r.status === 'resolved' && <Btn small tone="ghost" busy={busy} onClick={() => setStatus(r, 'open')}>Reabrir</Btn>}
          </div>
        </div>;
      })}
      {ordered.length > shown.length && <div className="tiny muted">Y {ordered.length - shown.length} más en el historial.</div>}
    </div>
    {resolving && <ReportResolveModal report={resolving} onClose={() => setResolving(null)} />}
  </>;
}

/** Cierre de reporte: nota opcional + quién lo reparó y cuánto cobró — con
    datos el servicio crea el unitService ligado (kind 'tires' si el reporte
    trae tirePosition) y el reporte gana repairedBy/repairCost. */
function ReportResolveModal({ report, onClose }: { report: UnitReport; onClose(): void }) {
  const { token, busy, error, mutate, dismiss } = usePortal();
  const [note, setNote] = useState(report.resolveNote ?? '');
  // Re-resolver (tras reabrir) sugiere lo ya capturado: el servicio solo
  // corrige los campos — el serviceId guardado evita duplicar el unitService.
  const [vendor, setVendor] = useState(report.repairedBy ?? '');
  const [cost, setCost] = useState(report.repairCost != null ? String(report.repairCost) : '');
  const costNum = cost.trim() ? Number(cost) : null;
  const costValid = costNum === null || (Number.isFinite(costNum) && costNum >= 0);
  const submit = async () => {
    const ok = await mutate(() => api('setUnitReportStatus', {
      reportId: report.id, status: 'resolved',
      note: note.trim() || undefined,
      vendor: vendor.trim() || undefined,
      cost: costNum ?? undefined,
    }, token), 'Reporte resuelto.');
    if (ok) onClose();
  };
  return <Modal title="Resolver reporte" onClose={onClose}>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <p className="small muted u-m0">{report.description}</p>
    <TextArea label="Nota de cierre (opcional)" value={note} onChange={e => setNote(e.target.value)} maxLength={500}
      placeholder="Ej. Se cambió la llanta por la de bodega, quedó al aire…" autoFocus />
    <div className="form-grid">
      <Field label="Lo reparó (taller/llantero)" value={vendor} onChange={e => setVendor(e.target.value)} maxLength={80}
        placeholder="Ej. Llantera El Compa" />
      <Field label="Costo ($)" type="number" min={0} inputMode="decimal" value={cost} onChange={e => setCost(e.target.value)}
        placeholder="Opcional" hint="Si el llantero cobró el parche, anótalo — se guarda como servicio de la unidad." />
    </div>
    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} disabled={!costValid} onClick={() => void submit()}>Resolver</Btn>
    </div>
  </Modal>;
}

/** Alta de reporte de falla desde la ficha (oficina): severidad con su
    consecuencia, «¿Dónde?» opcional (componentes y posiciones de llanta),
    descripción y foto con vista previa — el mismo payload que manda el
    chofer desde la app, más tirePosition cuando la falla es de una llanta. */
function ReportForm({ unit, onClose }: { unit: Unit; onClose(): void }) {
  const { token, busy, error, mutate, dismiss, data, authFail, fail } = usePortal();
  const company = data?.company ?? null;
  const [severity, setSeverity] = useState<UnitReportSeverity>('medium');
  const [where, setWhere] = useState('');
  const [description, setDescription] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoUrl, setPhotoUrl] = useState('');
  // La foto se procesa (photoFromFile) ANTES del mutate(): el inFlight del
  // contexto solo protege la llamada — sin bandera propia un doble clic
  // duplica la compresión y dispara dos altas seguidas.
  const [submitting, setSubmitting] = useState(false);

  // Vista previa local de la foto antes de guardar; el object URL se revoca
  // al cambiar de foto o cerrar el modal.
  useEffect(() => {
    if (!photo) { setPhotoUrl(''); return; }
    const url = URL.createObjectURL(photo);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const components = unit.components ?? [];
  const positions = tirePositionsForKind(unit.kind, unitCategoryOf(unit, company));

  // Severidad como chips con la consecuencia de cada una — copia corta y útil.
  const SEVERITY_OPTS: { v: UnitReportSeverity; label: string; desc: string }[] = [
    { v: 'low', label: 'Leve', desc: 'Puede seguir rodando — agenda revisión' },
    { v: 'medium', label: 'Media', desc: 'Necesita servicio pronto' },
    { v: 'high', label: 'Grave', desc: 'No debe rodar sin autorización' },
  ];

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      let desc = description.trim();
      let tirePosition: string | null = null;
      if (where.startsWith('tire:')) {
        tirePosition = where.slice(5);
      } else if (where.startsWith('cmp:')) {
        // El contrato solo agrega tirePosition: el componente viaja como
        // etiqueta al inicio de la descripción («[Reefer] …»).
        const cmp = components.find(c => c.id === where.slice(4));
        if (cmp) desc = `[${cmp.label || 'Componente'}] ${desc}`;
      }
      const input: UnitReportInput = {
        unitId: unit.id, severity, description: desc,
        ...(tirePosition ? { tirePosition } : {}),
        photo: photo ? await photoFromFile(photo) : null,
      };
      const ok = await mutate(() => api('saveUnitReport', { input }, token, 60000), 'Reporte registrado.');
      if (ok) onClose();
    } catch (e) {
      // Token muerto durante la compresión → authFail; otro fallo del prep se
      // reporta al banner en vez de morir como unhandled rejection.
      if (!authFail(e)) fail(e);
    } finally { setSubmitting(false); }
  };

  return <Modal title={`Reportar falla · ${unitName(unit)}`} onClose={onClose}>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <div className="form-grid one">
      <div className="field">
        <label id="rep-sev">Severidad</label>
        <div className="sev-opts" role="radiogroup" aria-labelledby="rep-sev">
          {SEVERITY_OPTS.map(o => <button key={o.v} type="button"
            className={`sev-opt${severity === o.v ? ' on' : ''}`} aria-pressed={severity === o.v}
            onClick={() => setSeverity(o.v)}>
            <span className="sev-name">{o.label}</span>
            <span className="sev-desc">{o.desc}</span>
          </button>)}
        </div>
      </div>
      {(components.length > 0 || positions.length > 0) && <Select label="¿Dónde? (opcional)" value={where} onChange={e => setWhere(e.target.value)}>
        <option value="">La unidad completa</option>
        {components.length > 0 && <optgroup label="Componentes">
          {components.map(c => <option key={c.id} value={`cmp:${c.id}`}>{c.label || 'Componente'}{c.brand ? ` (${[c.brand, c.model].filter(Boolean).join(' ')})` : ''}</option>)}
        </optgroup>}
        {positions.length > 0 && <optgroup label="Llantas">
          {positions.map(p => <option key={p.key} value={`tire:${p.key}`}>{p.label}</option>)}
        </optgroup>}
      </Select>}
      <Field label="Descripción de la falla" value={description} onChange={e => setDescription(e.target.value)} maxLength={1000}
        placeholder="Ej. Puerta trasera no sella, gotea línea de aire…" />
      <div className="field">
        <label htmlFor="rep-photo">Foto (opcional)</label>
        {/* El value se limpia tras leer: elegir el MISMO archivo otra vez
            vuelve a disparar change. */}
        <input className="input" id="rep-photo" type="file" accept="image/jpeg,image/png,image/webp,image/heic"
          onChange={e => { setPhoto(e.target.files?.[0] ?? null); e.target.value = ''; }} />
        {photoUrl && <img className="rep-thumb" src={photoUrl} alt="Vista previa de la foto del reporte" />}
        <span className="hint">JPEG, PNG, WebP o HEIC de máximo 4.5 MB.</span>
      </div>
    </div>
    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy || submitting} disabled={description.trim().length < 4} onClick={() => void submit()}>Registrar reporte</Btn>
    </div>
  </Modal>;
}

// ---------- Llantas por posición ----------

/** Direccional (eje delantero de vehículos): dir-* y del-* — su mínimo DOT
    es más alto (4/32) que el del resto de posiciones (2/32). */
const isSteerKey = (key: string) => /^dir-|^del-/.test(key);

/** Tono del punto de estado por profundidad del dibujo: ok ≥6/32 dir /
    ≥4/32 resto, ámbar cerca del mínimo, rojo bajo el mínimo DOT (4/32 dir,
    2/32 resto). Sin lectura → punto neutro. */
function tireTone(tire: UnitTire, steer: boolean): 'ok' | 'warn' | 'bad' | '' {
  const t = tire.tread32;
  if (t === null || !Number.isFinite(t)) return '';
  const ok = steer ? 6 : 4;
  const min = steer ? 4 : 2;
  return t < min ? 'bad' : t < ok ? 'warn' : 'ok';
}

/** Etiqueta corta de posición para la celda («Tracción 1 exterior izquierda»
    → «T1 ext izq»); la completa queda en el title/aria-label. */
function tireShortLabel(pos: TirePosition): string {
  return pos.label
    .replace('Direccional', 'Dir').replace('Delantera', 'Del').replace('Trasera', 'Tra')
    .replace('Tracción', 'T').replace('Eje', 'E')
    .replace('exterior', 'ext').replace('interior', 'int')
    .replace('izquierda', 'izq').replace('derecha', 'der');
}

/** Lado de la posición por su key: -r/-re/-ri → derecha; el resto izquierda. */
const tireSide = (key: string): 'left' | 'right' => /r[ei]?$/.test(key) ? 'right' : 'left';

/** Modo del diagrama: normal (hueco → «Montar», ocupada → su ficha), rotar
    (ocupadas acentuadas, origen marcado, segunda elección = destino) y
    montar de bodega (solo las vacías responden, las ocupadas se apagan). */
type TireDiagramMode = 'normal' | 'rotate' | 'mount';

/** Diagrama de llantas de la unidad: una fila por eje con las posiciones en
    espejo — sencillas centradas junto al chasis, dobles en las orillas
    (ext-int | int-ext). El modo decide qué celdas responden y a qué. */
function TireDiagram({ positions, tires, mode = 'normal', selectedKey, onMount, onOpen, onPick }: {
  positions: TirePosition[]; tires: UnitTire[];
  mode?: TireDiagramMode; selectedKey?: string | null;
  onMount(pos: TirePosition): void; onOpen(tire: UnitTire): void;
  onPick?(pos: TirePosition): void;
}) {
  const byPosition = new Map(tires.map(t => [t.position, t]));
  const axles = useMemo(() => {
    const map = new Map<number, TirePosition[]>();
    for (const p of positions) map.set(p.axle, [...(map.get(p.axle) ?? []), p]);
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [positions]);
  const cell = (pos: TirePosition) => {
    const tire = byPosition.get(pos.key);
    if (!tire) {
      // Hueco: en rotar solo responde como destino una vez elegido el
      // origen; en montar es lo único vivo del diagrama.
      const armed = mode === 'mount' || (mode === 'rotate' && !!selectedKey);
      return <button type="button" key={pos.key}
        className={`tire-cell empty${mode === 'normal' ? '' : armed ? ' pick' : ' dim'}`}
        disabled={mode !== 'normal' && !armed}
        title={`${pos.label} — posición vacía`}
        aria-label={`${pos.label}, vacía${mode === 'normal' ? ' — montar llanta' : armed ? ' — elegir destino' : ''}`}
        onClick={() => mode === 'normal' ? onMount(pos) : onPick?.(pos)}>
        <span className="tire-pos">{tireShortLabel(pos)}</span>
        {mode === 'normal' ? <span className="tire-mount">Montar</span>
          : armed ? <span className="tire-mount">Aquí</span> : null}
      </button>;
    }
    const tone = tireTone(tire, isSteerKey(pos.key));
    const cls = mode === 'rotate' ? ` pick${selectedKey === pos.key ? ' sel' : ''}` : mode === 'mount' ? ' dim' : '';
    return <button type="button" key={pos.key} className={`tire-cell${cls}`}
      disabled={mode === 'mount'} title={pos.label}
      aria-pressed={mode === 'rotate' ? selectedKey === pos.key : undefined}
      onClick={() => mode === 'normal' ? onOpen(tire) : onPick?.(pos)}>
      <span className="tire-cell-top">
        <span className={`tire-dot${tone ? ` ${tone}` : ''}`} aria-hidden="true" />
        <span className="tire-pos">{tireShortLabel(pos)}</span>
        {tire.retread && <span className="tire-rec">REC</span>}
      </span>
      <span className="tire-main">{[tire.brand, tire.size].filter(Boolean).join(' ') || 'Llanta'}</span>
      <span className="tire-sub">{[tire.tread32 !== null ? `${tire.tread32}/32` : '', tire.dot ? `DOT ${tire.dot}` : ''].filter(Boolean).join(' · ') || 'Sin profundidad'}</span>
    </button>;
  };
  return <div className="tire-diagram" role="group" aria-label="Llantas por posición">
    {axles.map(([n, list]) => {
      const dual = list.length > 2;
      return <div className="tire-axle" key={n}>
        <span className="tire-axle-tag">Eje {n}{list.length <= 2 ? ' · sencillas' : ' · dobles'}</span>
        <span className="tire-track">
          <span className={`tire-side left${dual ? ' dual' : ''}`}>{list.filter(p => tireSide(p.key) === 'left').map(cell)}</span>
          <span className="tire-spine" aria-hidden="true" />
          <span className={`tire-side right${dual ? ' dual' : ''}`}>{list.filter(p => tireSide(p.key) === 'right').map(cell)}</span>
        </span>
      </div>;
    })}
  </div>;
}

/** Alta/edición de llanta en una posición (saveUnitTire): marca, modelo,
    medida, profundidad /32, DOT, reencauche, fecha y lectura del contador al
    montar. La posición queda fija — cambiarla es desmontar y montar de nuevo.
    En edición ofrece «Desmontar» (TireRemoveModal) en el pie.
    Con `editing` desmontada (removedAt) es REMONTAJE desde bodega: el casco
    llega prefijado y lo editable son sus datos; fecha/contador sugieren el
    momento actual y el servicio limpia removedAt al guardar. */
function TireForm({ unit, position, editing, onDone, onClose }: {
  unit: Unit; position: TirePosition; editing: UnitTire | null;
  onDone(): void; onClose(): void;
}) {
  const { token, busy, error, mutate, dismiss, data } = usePortal();
  const isRemount = editing?.removedAt != null;
  // El casco pudo desmontarse de OTRA unidad: «salió de» se rotula con el
  // kind del origen — el de la unidad destino puede numerar distinto las
  // posiciones (ej. de una caja a un tracto).
  const remountSrc = isRemount && editing
    ? ((data?.companyUnits ?? []).find(u => u.id === editing.unitId) ?? unit)
    : unit;
  const [brand, setBrand] = useState(editing?.brand ?? '');
  const [model, setModel] = useState(editing?.model ?? '');
  const [size, setSize] = useState(editing?.size ?? '');
  // La profundidad del casco remontable es la final del desmontaje.
  const [tread, setTread] = useState(editing ? String((isRemount ? editing.removedTread32 ?? editing.tread32 : editing.tread32) ?? '') : '');
  const [dot, setDot] = useState(editing?.dot ?? '');
  const [retread, setRetread] = useState(editing?.retread ?? false);
  const [installedAt, setInstalledAt] = useState(editing && !isRemount && editing.installedAt ? isoToDateInput(editing.installedAt) : new Date().toISOString().slice(0, 10));
  const meterIn = useMeterInput(unit.meterKind,
    isRemount ? (unit.meter ? String(unit.meter.value) : '')
    : editing?.installedMeter != null ? String(editing.installedMeter) : '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [removing, setRemoving] = useState(false);

  const meterUnit = METER_UNIT[unit.meterKind];
  const treadNum = tread.trim() ? Number(tread) : null;
  const treadValid = treadNum === null || (Number.isFinite(treadNum) && treadNum >= 0 && treadNum <= 40);
  const valid = (brand.trim() || size.trim()).length > 0 && treadValid && meterIn.valid;

  const submit = async () => {
    const input: UnitTireInput = {
      unitId: unit.id, position: position.key,
      brand: brand.trim(), model: model.trim(), size: size.trim(),
      tread32: treadNum, dot: dot.trim(), retread,
      installedAt: dateInputToIso(installedAt), installedMeter: meterIn.value,
      notes: notes.trim(),
    };
    // CONFLICT (posición ya ocupada) cae en el aviso global del contexto.
    // tireId de una desmontada = remontaje: el servicio limpia removedAt/*
    // y fija la nueva posición con los datos del casco ya editados.
    const ok = await mutate(() => api('saveUnitTire', { input, ...(editing ? { tireId: editing.id } : {}) }, token),
      isRemount ? 'Casco remontado.' : editing ? 'Llanta actualizada.' : 'Llanta montada.');
    if (ok) { onDone(); onClose(); }
  };

  if (removing && editing && !isRemount) return <TireRemoveModal position={position} tire={editing} onDone={onDone} onClose={onClose} />;

  return <Modal title={`${editing && !isRemount ? 'Llanta' : 'Montar llanta'} · ${position.label}`} onClose={onClose}>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    {isRemount && editing && <p className="small muted u-m0">
      Casco de bodega — salió de «{tirePositionLabel(remountSrc.kind, unitCategoryOf(remountSrc, data?.company), editing.position)}» el {dateOnly(editing.removedAt)} y se remonta aquí.
    </p>}
    {editing && !isRemount && <p className="small muted u-m0">
      Montada {dateOnly(editing.installedAt)}{editing.installedMeter !== null ? ` a los ${editing.installedMeter.toLocaleString('es-MX')} ${meterUnit}` : ''} por {editing.createdByName || 'la oficina'}.
    </p>}
    <div className="form-grid">
      <Field label="Marca" value={brand} onChange={e => setBrand(e.target.value)} maxLength={40} placeholder="Ej. Michelin" autoFocus={!editing} />
      <Field label="Modelo" value={model} onChange={e => setModel(e.target.value)} maxLength={40} placeholder="Ej. XZA3" />
      <Field label="Medida" value={size} onChange={e => setSize(e.target.value)} maxLength={20} placeholder="Ej. 295/75R22.5" />
      <Field label="Profundidad (/32)" type="number" min={0} max={40} inputMode="numeric" value={tread} onChange={e => setTread(e.target.value)}
        placeholder="Ej. 12" hint={isSteerKey(position.key) ? 'Mínimo DOT en direccional: 4/32.' : 'Mínimo DOT: 2/32.'} />
      <Field label="DOT" value={dot} onChange={e => setDot(e.target.value)} maxLength={8} placeholder="Semana/año — ej. 2623" />
      {unit.meterKind !== 'none' && <div className="field">
        <label htmlFor="tire-meter">Contador al montar ({meterIn.label || meterUnit})</label>
        <div className="row">
          <input className="input num" id="tire-meter" type="number" min={0} inputMode="numeric"
            value={meterIn.text} onChange={e => meterIn.setText(e.target.value)}
            placeholder={unit.meter ? `Actual ${meterIn.toCapture(unit.meter.value).toLocaleString('es-MX')} ${meterIn.label}` : 'Lectura del odómetro'} />
          {meterIn.seg}
        </div>
        {meterIn.hint && <span className="hint">{meterIn.hint}</span>}
      </div>}
    </div>
    <Field label="Fecha de montaje" type="date" value={installedAt} onChange={e => setInstalledAt(e.target.value)} />
    <label className="insp-review"><input type="checkbox" checked={retread} onChange={e => setRetread(e.target.checked)} />
      <span>Llanta reencauchada (REC)</span></label>
    <TextArea label="Notas" value={notes} onChange={e => setNotes(e.target.value)} maxLength={500}
      placeholder="Ej. Garantía vigente, parche previo en el costado…" />
    <div className="actions end modal-actions">
      {editing && !isRemount && <Btn small tone="danger" busy={busy} style={{ marginRight: 'auto' }} onClick={() => setRemoving(true)}>Desmontar</Btn>}
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} disabled={!valid} onClick={() => void submit()}>{editing && !isRemount ? 'Guardar cambios' : 'Montar llanta'}</Btn>
    </div>
  </Modal>;
}

/** Desmontaje de llanta (removeUnitTire): motivo en chips, profundidad final
    opcional, nota y «¿Dónde queda?» — el casco puede seguir en bodega o
    reencauche (stock remontable) o salir del inventario (desecho/robo). */
function TireRemoveModal({ position, tire, onDone, onClose }: {
  position: TirePosition; tire: UnitTire; onDone(): void; onClose(): void;
}) {
  const { token, busy, error, mutate, dismiss } = usePortal();
  const [reason, setReason] = useState<TireRemoveReason>('worn');
  const [location, setLocation] = useState<TireLocation>(defaultTireLocation('worn'));
  const [tread, setTread] = useState(tire.tread32 != null ? String(tire.tread32) : '');
  const [note, setNote] = useState('');
  const treadNum = tread.trim() ? Number(tread) : null;
  const treadValid = treadNum === null || (Number.isFinite(treadNum) && treadNum >= 0 && treadNum <= 40);
  // El destino sugiere solo: al cambiar el motivo se repone su default.
  const pickReason = (r: TireRemoveReason) => { setReason(r); setLocation(defaultTireLocation(r)); };
  const submit = async () => {
    const ok = await mutate(() => api('removeUnitTire', { tireId: tire.id, reason, tread32: treadNum, note: note.trim(), location }, token),
      'Llanta desmontada — queda en el historial.');
    if (ok) { onDone(); onClose(); }
  };
  return <Modal title={`Desmontar llanta · ${position.label}`} onClose={onClose}>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <p className="small muted u-m0">{[tire.brand, tire.size].filter(Boolean).join(' ') || 'La llanta'} sale de la posición — la posición queda libre y la llanta pasa al historial con su motivo.</p>
    <div className="field">
      <label id="trm-reason">Motivo del desmontaje</label>
      <div className="chips" role="radiogroup" aria-labelledby="trm-reason">
        {(Object.keys(TIRE_REASON) as TireRemoveReason[]).map(r => <button key={r} type="button"
          className={`chip${reason === r ? ' on' : ''}`} aria-pressed={reason === r} onClick={() => pickReason(r)}>{TIRE_REASON[r]}</button>)}
      </div>
    </div>
    <div className="field">
      <label id="trm-loc">¿Dónde queda?</label>
      <div className="chips" role="radiogroup" aria-labelledby="trm-loc">
        {(['yard', 'retread', 'scrap', 'gone'] as TireLocation[]).map(l => <button key={l} type="button"
          className={`chip${location === l ? ' on' : ''}`} aria-pressed={location === l} onClick={() => setLocation(l)}>{TIRE_LOCATION[l].label}</button>)}
      </div>
      <span className="hint">{location === 'yard' || location === 'retread'
        ? 'Queda como casco recuperable — se puede remontar desde la bodega.'
        : 'Sale del inventario — ya no aparece en la bodega.'}</span>
    </div>
    <Field label="Profundidad final (/32, opcional)" type="number" min={0} max={40} inputMode="numeric"
      value={tread} onChange={e => setTread(e.target.value)} placeholder={tire.tread32 !== null ? String(tire.tread32) : 'Ej. 3'} />
    <TextArea label="Nota (opcional)" value={note} onChange={e => setNote(e.target.value)} maxLength={300}
      placeholder="Ej. Se mandó a reencauche con la pila de la bodega…" />
    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn tone="danger" busy={busy} disabled={!treadValid} onClick={() => void submit()}>Desmontar llanta</Btn>
    </div>
  </Modal>;
}

/** Confirmación de rotación/movida (swapUnitTires): «origen → destino» con
    las llantas involucradas — destino vacío = la llanta solo se mueve. */
function TireSwapModal({ unit, from, to, tireFrom, tireTo, onDone, onClose }: {
  unit: Unit; from: TirePosition; to: TirePosition;
  tireFrom: UnitTire | null; tireTo: UnitTire | null;
  onDone(): void; onClose(): void;
}) {
  const { token, busy, error, mutate, dismiss } = usePortal();
  const tireName = (t: UnitTire | null) => t ? [t.brand, t.size].filter(Boolean).join(' ') || 'la llanta' : 'la llanta';
  const submit = async () => {
    const ok = await mutate(() => api('swapUnitTires', { unitId: unit.id, from: from.key, to: to.key }, token),
      tireTo ? 'Llantas intercambiadas.' : 'Llanta movida.');
    if (ok) onDone();
  };
  return <Modal title="Rotar llantas" onClose={onClose}>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <p className="small u-m0">
      {tireTo
        ? <>Intercambiar <b>{tireName(tireFrom)}</b> de «{from.label}» con <b>{tireName(tireTo)}</b> de «{to.label}».</>
        : <>Mover <b>{tireName(tireFrom)}</b> de «{from.label}» a «{to.label}» — la posición queda libre.</>}
    </p>
    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} onClick={() => void submit()}>{tireTo ? 'Intercambiar' : 'Mover'}</Btn>
    </div>
  </Modal>;
}

/** «Mover a…» de un casco de bodega (setTireLocation): bodega y reencauche
    lo mantienen remontable; desecho y robo/pérdida lo sacan del stock. */
function TireLocationModal({ tire, onDone, onClose }: { tire: UnitTire; onDone(): void; onClose(): void }) {
  const { token, busy, error, mutate, dismiss } = usePortal();
  const [location, setLocation] = useState<TireLocation>(tire.location === 'retread' ? 'retread' : 'yard');
  const submit = async () => {
    const ok = await mutate(() => api('setTireLocation', { tireId: tire.id, location }, token), 'Ubicación del casco actualizada.');
    if (ok) { onDone(); onClose(); }
  };
  return <Modal title={`Destino del casco · ${[tire.brand, tire.size].filter(Boolean).join(' ') || 'llanta'}`} onClose={onClose}>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <div className="field">
      <div className="chips" role="radiogroup" aria-label="Destino del casco">
        {(['yard', 'retread', 'scrap', 'gone'] as TireLocation[]).map(l => <button key={l} type="button"
          className={`chip${location === l ? ' on' : ''}`} aria-pressed={location === l} onClick={() => setLocation(l)}>{TIRE_LOCATION[l].label}</button>)}
      </div>
      <span className="hint">{location === 'yard' || location === 'retread'
        ? 'Sigue en el stock — se puede remontar en cualquier unidad.'
        : 'Sale del stock — ya no se puede remontar.'}</span>
    </div>
    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} onClick={() => void submit()}>Guardar</Btn>
    </div>
  </Modal>;
}

/** Tarjeta de componente dentro de la ficha: nombre, serie, contador propio
    (horas del reefer) con lectura manual, historial filtrado y su servicio. */
function ComponentCard({ unit, component, readings, memberName }: {
  unit: Unit; component: UnitComponent; readings: MeterReading[] | null;
  memberName(uid: string): string;
}) {
  const { token, busy, mutate } = usePortal();
  const meterIn = useMeterInput(component.meterKind);
  const name = component.label || (component.kind === 'reefer' ? 'Reefer' : 'Componente');
  const meterUnit = METER_UNIT[component.meterKind];
  const due = serviceDue(component);
  const lastValue = component.meter?.value ?? null;
  const readingValue = meterIn.value;
  const readingValid = readingValue !== null && (lastValue === null || readingValue >= lastValue);
  const save = () => {
    if (readingValue === null) return;
    void mutate(() => api('saveMeterReading', { unitId: unit.id, componentId: component.id, value: readingValue }, token), 'Lectura del componente registrada.')
      .then(ok => { if (ok) meterIn.setText(''); });
  };
  const own = (readings ?? []).filter(r => r.componentId === component.id);
  return <div className="ucard-detail">
    <div className="row between multiline">
      <div>
        <div className="title sm">{name}{[component.brand, component.model].filter(Boolean).length ? ` · ${[component.brand, component.model].filter(Boolean).join(' ')}` : ''}</div>
        <div className="tiny muted">{[component.serial ? `Serie ${component.serial}` : '', component.meter ? `${component.meter.value.toLocaleString('es-MX')} ${meterUnit} · ${dateTime(component.meter.at)}` : 'Sin lecturas'].filter(Boolean).join(' · ')}</div>
      </div>
      <Icon name={component.kind === 'reefer' ? 'snow' : 'wrench'} size={18} />
    </div>
    {due.length > 0 && <div className="small">{due.map((d, i) => <span key={i} className={d.overdue ? 'overdue' : 'warn'}>{d.text}</span>)}</div>}
    {component.meterKind !== 'none' && <>
      <div className="row">
        <input className="input num wide" type="number" min={0} inputMode="numeric"
          placeholder={lastValue !== null ? `≥ ${meterIn.toCapture(lastValue).toLocaleString('es-MX')} ${meterIn.label}` : `${name} (${meterIn.label})`}
          value={meterIn.text} onChange={e => meterIn.setText(e.target.value)} aria-label={`Lectura de ${name}`} />
        {meterIn.seg ?? <span className="small muted">{meterUnit}</span>}
        <Btn small busy={busy} disabled={!readingValid} onClick={save}>Guardar</Btn>
      </div>
      {meterIn.hint && <span className="hint">{meterIn.hint}</span>}
      {own.length > 0 && <div className="list">
        {own.slice(0, 5).map(r => <div className="item" key={r.id}>
          <div className="row between multiline">
            <span className="small name">{Number.isFinite(r.value) ? `${r.value.toLocaleString('es-MX')} ${meterUnit}` : '—'}</span>
            <span className="tiny muted">{dateTime(r.at)}</span>
          </div>
          <div className="tiny muted">{memberName(r.reportedBy)} · {METER_SOURCE[r.source] ?? r.source}</div>
        </div>)}
        {own.length > 5 && <div className="tiny muted">Y {own.length - 5} más en el historial.</div>}
      </div>}
    </>}
  </div>;
}

/** Alta/edición de unidad (saveUnit). Tipo con optgroups por categoría +
    tipos personalizados de la empresa + «Otro tipo…»; placas por país en
    filas dinámicas; caja refrigerada lleva su reefer como componente. */
export function UnitForm({ unit, units, onClose }: { unit?: Unit; units: Unit[]; onClose(): void }) {
  const { token, busy, error, mutate, dismiss, data } = usePortal();
  const company = data?.company ?? null;
  const customs: CustomUnitKind[] = company?.customUnitKinds ?? [];
  const [kind, setKind] = useState<string>(unit?.kind ?? 'tractor');
  const [internalNumber, setInternalNumber] = useState(unit?.internalNumber ?? '');
  const [plates, setPlates] = useState<UnitPlate[]>(unit?.plates.length ? unit.plates : [{ country: 'MX', number: '' }]);
  const [brand, setBrand] = useState(unit?.brand ?? '');
  const [model, setModel] = useState(unit?.model ?? '');
  const [year, setYear] = useState(unit?.year ? String(unit.year) : '');
  const [vin, setVin] = useState(unit?.vin ?? '');
  const [owner, setOwner] = useState<Unit['owner']>(unit?.owner ?? 'company');
  const [meterKind, setMeterKind] = useState<UnitMeterKind>(unit?.meterKind ?? 'km');
  const [parentUnitId, setParentUnitId] = useState(unit?.parentUnitId ?? '');
  const [meterInterval, setMeterInterval] = useState(unit?.maintenanceMeterInterval ? String(unit.maintenanceMeterInterval) : '');
  const [daysInterval, setDaysInterval] = useState(unit?.maintenanceDaysInterval ? String(unit.maintenanceDaysInterval) : '');
  // «Otro tipo…»: nombre libre + categoría → el servicio lo registra en la empresa.
  const [newKindLabel, setNewKindLabel] = useState('');
  const [newKindCategory, setNewKindCategory] = useState<UnitCategory>('attachment');
  // Reefer de la caja refrigerada: viaja como componente del conjunto.
  const reeferComponent = unit?.components?.find(c => c.kind === 'reefer');
  const [rfBrand, setRfBrand] = useState(reeferComponent?.brand ?? '');
  const [rfModel, setRfModel] = useState(reeferComponent?.model ?? '');
  const [rfSerial, setRfSerial] = useState(reeferComponent?.serial ?? '');
  const [rfMeterInterval, setRfMeterInterval] = useState(reeferComponent?.maintenanceMeterInterval ? String(reeferComponent.maintenanceMeterInterval) : '');
  const [rfDaysInterval, setRfDaysInterval] = useState(reeferComponent?.maintenanceDaysInterval ? String(reeferComponent.maintenanceDaysInterval) : '');

  const isNewKind = kind === '__new__';
  const category: UnitCategory = isNewKind ? newKindCategory : unitCategoryOf({ kind }, company);
  // El equipo cuelga de un remolque de la empresa (reefer suelto, genset).
  const parents = units.filter(u => unitCategoryOf(u, company) === 'attachment' && u.status !== 'sold' && u.id !== unit?.id);
  // El remolque actual pudo venderse después: si su id quedó en el valor
  // pero no en las opciones, el select fingiría «Sin remolque fijo».
  const missingParent = parentUnitId && !parents.some(u => u.id === parentUnitId)
    ? units.find(u => u.id === parentUnitId) : undefined;
  const usedCountries = new Set(plates.map(p => p.country));

  const pickKind = (next: string) => {
    setKind(next);
    // Sugerencia de contador por categoría en el alta.
    if (!unit) {
      const cat = next === '__new__' ? newKindCategory : unitCategoryOf({ kind: next }, company);
      setMeterKind(cat === 'vehicle' ? 'km' : cat === 'equipment' ? 'hours' : 'none');
    }
  };

  const patchPlate = (i: number, part: Partial<UnitPlate>) =>
    setPlates(list => list.map((p, j) => j === i ? { ...p, ...part } : p));

  const cleanPlates = plates.filter(p => p.number.trim()).map(p => ({ country: p.country, number: p.number.trim().toUpperCase() }));
  const yearNum = year.trim() ? Math.trunc(Number(year)) : null;
  const meterInt = meterInterval.trim() ? Math.trunc(Number(meterInterval)) : null;
  const daysInt = daysInterval.trim() ? Math.trunc(Number(daysInterval)) : null;
  // Misma regla del servicio: vehículos y remolques circulan → ≥1 placa;
  // el equipo puede ir sin placa.
  const needsPlate = category !== 'equipment';
  const valid = internalNumber.trim().length > 0 && (!needsPlate || cleanPlates.length > 0)
    && (!isNewKind || newKindLabel.trim().length > 1)
    && (yearNum === null || (Number.isFinite(yearNum) && yearNum >= 1950 && yearNum <= 2100))
    && (meterInt === null || (Number.isFinite(meterInt) && meterInt > 0))
    && (daysInt === null || (Number.isFinite(daysInt) && daysInt > 0));

  const submit = async () => {
    const rfTouched = rfBrand.trim() || rfModel.trim() || rfSerial.trim() || rfMeterInterval.trim() || rfDaysInterval.trim();
    const components: UnitInput['components'] = kind === 'cajaRefrigerada' && (rfTouched || reeferComponent)
      ? [{
          id: reeferComponent?.id, kind: 'reefer', label: 'Reefer',
          brand: rfBrand.trim(), model: rfModel.trim(), serial: rfSerial.trim(), meterKind: 'hours',
          maintenanceMeterInterval: rfMeterInterval.trim() ? Number(rfMeterInterval) : null,
          maintenanceDaysInterval: rfDaysInterval.trim() ? Number(rfDaysInterval) : null,
        }]
      : undefined;
    const input: UnitInput = {
      kind: isNewKind ? 'ck-custom' : kind as UnitInput['kind'],
      newKind: isNewKind ? { label: newKindLabel.trim(), category: newKindCategory } : undefined,
      plates: cleanPlates,
      internalNumber: internalNumber.trim(),
      brand: brand.trim(), model: model.trim(),
      year: yearNum && Number.isFinite(yearNum) ? yearNum : null,
      vin: vin.trim().toUpperCase(),
      owner, meterKind,
      parentUnitId: category === 'equipment' ? parentUnitId || null : null,
      components,
      maintenanceMeterInterval: meterKind === 'none' ? null : (meterInt && meterInt > 0 ? meterInt : null),
      maintenanceDaysInterval: daysInt && daysInt > 0 ? daysInt : null,
    };
    const ok = await mutate(() => api('saveUnit', { input, ...(unit ? { unitId: unit.id } : {}) }, token),
      unit ? 'Unidad actualizada.' : 'Unidad registrada.');
    if (ok) onClose();
  };

  const kindGroups: { cat: UnitCategory; label: string }[] = [
    { cat: 'vehicle', label: 'Vehículos' },
    { cat: 'attachment', label: 'Remolques' },
    { cat: 'equipment', label: 'Equipo' },
  ];

  return <Modal title={unit ? `Editar ${unitName(unit, company)}` : 'Agregar unidad'} onClose={onClose} wide>
    {error && <Notice tone="danger" onClose={dismiss}>{error}</Notice>}
    <div className="form-grid">
      <Select label="Tipo de unidad" value={kind} onChange={e => pickKind(e.target.value)} autoFocus>
        {kindGroups.map(g => {
          // Equipo integrado solo al editar una unidad legada de ese tipo:
          // en el alta los reefers nacen dentro de la caja refrigerada (el
          // conjunto viaja junto) — equipo suelto = «Otro tipo…» + categoría.
          const builtins = Object.entries(UNIT_KIND).filter(([value, meta]) => meta.category === g.cat && (g.cat !== 'equipment' || value === unit?.kind));
          const customKinds = customs.filter(k => k.category === g.cat);
          if (!builtins.length && !customKinds.length) return null;
          return <optgroup key={g.cat} label={g.label}>
            {builtins.map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}
            {customKinds.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
          </optgroup>;
        })}
        <option value="__new__">Otro tipo…</option>
      </Select>
      {isNewKind && <>
        <Field label="Nombre del tipo" value={newKindLabel} onChange={e => setNewKindLabel(e.target.value)} maxLength={50}
          placeholder="Ej. Dolly, remolque de emergencia…" hint="Queda disponible para futuras unidades de la empresa." />
        <Select label="Categoría del tipo" value={newKindCategory} onChange={e => { setNewKindCategory(e.target.value as UnitCategory); if (!unit) setMeterKind(e.target.value === 'equipment' ? 'hours' : e.target.value === 'vehicle' ? 'km' : 'none'); }}
          hint="Vehículos y remolques circulan: llevan al menos una placa. El equipo cuelga de un remolque.">
          <option value="vehicle">Vehículo (se mueve solo)</option>
          <option value="attachment">Remolque (se remolca)</option>
          <option value="equipment">Equipo (cuelga de un remolque)</option>
        </Select>
      </>}
      {category === 'equipment' && <Select label="Remolque al que pertenece" value={parentUnitId} onChange={e => setParentUnitId(e.target.value)}
        hint="El equipo viaja con su remolque: al asignarlo a un viaje, el equipo va incluido.">
        <option value="">Sin remolque fijo</option>
        {parents.map(u => <option key={u.id} value={u.id}>{unitName(u, company)}</option>)}
        {missingParent && <option value={missingParent.id}>{unitName(missingParent, company)} (vendida)</option>}
      </Select>}
      <Field label="Número interno" value={internalNumber} onChange={e => setInternalNumber(e.target.value)} maxLength={20}
        placeholder="Ejemplo: T-01, C-53-01" hint="Clave de la flota — sale en viajes y documentos." />
      <Field label="Marca" value={brand} onChange={e => setBrand(e.target.value)} maxLength={40} placeholder="Ej. Freightliner" />
      <Field label="Modelo" value={model} onChange={e => setModel(e.target.value)} maxLength={40} placeholder="Ej. Cascadia" />
      <Field label="Año" type="number" min={1950} max={2100} inputMode="numeric" value={year} onChange={e => setYear(e.target.value)} placeholder="Ej. 2021" />
      <Field label="VIN / serie" value={vin} onChange={e => setVin(e.target.value)} maxLength={30} placeholder="Opcional" />
      <Select label="Propiedad" value={owner} onChange={e => setOwner(e.target.value as Unit['owner'])}>
        <option value="company">Propia</option>
        <option value="client">Del cliente</option>
        <option value="third">De tercero</option>
      </Select>
      <Select label="Contador" value={meterKind} onChange={e => setMeterKind(e.target.value as UnitMeterKind)}
        hint="Vehículos en km o millas (odómetros de US leen mi); el equipo (reefer, genset) en horas; los remolques pueden quedar sin contador.">
        <option value="km">Kilómetros</option>
        <option value="mi">Millas</option>
        <option value="hours">Horas de motor</option>
        <option value="none">Sin contador</option>
      </Select>
      {meterKind !== 'none' && <Field label={`Servicio cada N ${METER_UNIT[meterKind]}`} type="number" min={1} inputMode="numeric"
        value={meterInterval} onChange={e => setMeterInterval(e.target.value)} placeholder="Ej. 20000" />}
      <Field label="Servicio cada N días" type="number" min={1} inputMode="numeric"
        value={daysInterval} onChange={e => setDaysInterval(e.target.value)} placeholder="Ej. 180" />
    </div>

    {kind === 'cajaRefrigerada' && <div className="field" role="group" aria-labelledby="uf-reefer">
      <label id="uf-reefer">Reefer de la caja (viaja como conjunto)</label>
      <div className="form-grid">
        <Field label="Marca del reefer" value={rfBrand} onChange={e => setRfBrand(e.target.value)} maxLength={40} placeholder="Thermo King, Carrier…" />
        <Field label="Modelo del reefer" value={rfModel} onChange={e => setRfModel(e.target.value)} maxLength={40} placeholder="Ej. Precedent C-600" />
        <Field label="Serie del reefer" value={rfSerial} onChange={e => setRfSerial(e.target.value)} maxLength={40} placeholder="Opcional" />
        <Field label="Servicio del reefer cada N horas" type="number" min={1} inputMode="numeric" value={rfMeterInterval} onChange={e => setRfMeterInterval(e.target.value)} placeholder="Ej. 3000" />
        <Field label="Servicio del reefer cada N días" type="number" min={1} inputMode="numeric" value={rfDaysInterval} onChange={e => setRfDaysInterval(e.target.value)} placeholder="Ej. 180" />
      </div>
      <span className="hint">El reefer lleva su propio contador de horas y sus documentos van con la caja.</span>
    </div>}

    <div className="field" role="group" aria-labelledby="uf-plates">
      <label id="uf-plates">Placas{needsPlate ? '' : ' (opcional en este tipo)'}</label>
      {plates.map((p, i) => <div className="row" key={i}>
        <select className="select u-w110" value={p.country} aria-label="País de la placa"
          onChange={e => patchPlate(i, { country: e.target.value as UnitPlate['country'] })}>
          {(['MX', 'US', 'CA'] as const).map(c => <option key={c} value={c} disabled={usedCountries.has(c) && p.country !== c}>{c}</option>)}
        </select>
        <input className="input grow" value={p.number} maxLength={20} placeholder="Número de placa"
          aria-label="Número de placa" autoCapitalize="characters" onChange={e => patchPlate(i, { number: e.target.value })} />
        <Btn small tone="ghost" onClick={() => setPlates(list => list.filter((_, j) => j !== i))} disabled={plates.length <= 1}>Quitar</Btn>
      </div>)}
      <div className="row">
        <Btn small tone="secondary" onClick={() => setPlates(list => [...list, { country: usedCountries.has('US') ? 'CA' : 'US', number: '' }])}
          disabled={plates.length >= 3}>Agregar placa</Btn>
        <span className="hint">Unidad de cruce: placa MX y US.{needsPlate ? ' Al menos una es obligatoria.' : ''}</span>
      </div>
    </div>

    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} disabled={!valid} onClick={() => void submit()}>{unit ? 'Guardar cambios' : 'Registrar unidad'}</Btn>
    </div>
  </Modal>;
}
